/**
 * PORT (wide screens): how the original's 807x600 screens fill the margin a wide screen shows around the stage
 * (Stage.visibleRect: BrowserHost `wide`, src/shared/wide-view.ts). The original was made for its window: whatever lay
 * beyond it was never seen, some of it on purpose (a button the build switched off, a data field, the tails of a
 * list's rows). Each screen of the original, when it is put on the stage, is "dressed" once:
 *  - its own children are clipped to the stage rectangle ($designClip), so nothing the original hid beyond the window
 *    appears or takes a tap (a data field, the tails of a list's rows), and the window's content looks exactly as
 *    before; except the few controls beyond the window that the port's features use (the main map's auction, replay
 *    and wormpedia buttons), shown in the margin where there is room;
 *  - what fills the margin: flat backgrounds and dims stretched over the whole shown area; a dim with panels on it
 *    drawn as one flat dim over the whole shown area, its panels as extension art; a flat panel cut in three, its frame
 *    moved out to the shown edges and its body stretched between (the frame then borders the whole screen); and
 *    pictures continued by the port's extension art (shapes composed from the scene's own art in the asset pipeline:
 *    newRasterShape patches, tools/extract/lib/asset-patches.mjs), drawn beyond the window's sides.
 * Every dressed screen is laid out again when the shown area changes (Event.RESIZE of the stage). Without a wide host
 * the shown area is the stage: stretched parts keep their size and the extension parts are hidden.
 *
 * The HUD (the main map's top bar and corner buttons, the battle's log, team bars, timer and icons; `hud`): with a wide
 * host (phones, tablets, the app) its parts are gathered in groups that move to the corners (and the top's middle) of
 * the part of the shown safe area the screen paints, and grow (the original's 10-30 px controls are 7-20 CSS px on a
 * phone) towards 48 CSS px touch targets (Stage.cssScale; on a tablet, where they measure that already, still a
 * little: `min`), as far as the room allows: never over the scene's key objects (`keep`: the tower, the shop, the
 * arsenal, the sun's disc) more than the original did (an empty corner of a group's box, `notch`, not counted), over
 * each other, or (a battle) over the touch controls' clusters (FWP.touch_zones), whose outer edges the battle's groups
 * line up with (the scanner 16 CSS px from them, `clear`: beyond the touch slop of a finger that missed a touch
 * button). A group placed next to another takes, of its places, the one where it grows most; a group may give room to
 * the groups after it (`yieldTo`: the player's plate to the icons under it, the timer to the team names); a group with
 * nothing shown (no replay to save, no achievement to announce) takes no room, and the HUD is laid out again when that
 * changes. A group gathers the original's own objects, so the game keeps writing their text, frames and bar widths,
 * and their hit areas scale with their art; a button whose art stays smaller (the small buttons of a plate, music and
 * sound on a small phone) gets a hit area of its own of 48 CSS px beyond its art (`hit`), which the groups placed after
 * it keep clear of, and the battle's log narrows its lines, then shows fewer of them (`wrap`, the newest shown), rather
 * than shrinking its text; a team name wider than its group keeps room for first takes the room left of it (up to the
 * log at its narrowest), then ends with an ellipsis (`fit`). A text lines up with the other groups by its glyphs, not
 * its field's gutters (the log's left edge, the version's corner, the timer's digits right-aligned under the wind
 * bar's end: `alignRight`); the gold plate stays in step with the player's plate (`step`). The battle's HUD is hidden
 * under the weapon sheet and the scanner (`under`: none of it shows beside a menu that does not reach the shown area's
 * top, a tablet's), and the log over the battlefield lies on a backdrop and folds to its newest lines (`fold`:
 * ui/battle-log.ts).
 * In touch mode the battle's weapon-sheet backpack is hidden, with a wide host or without one: the touch controls have
 * that button. The battle's team names and timer are outlined (`outline`).
 * The battle's groups fade while the current worm is under what they show (FWP.hud_focus; shownArea: the log's lines,
 * the team bars' rows); the camera keeps it clear of the touch controls' clusters (FWP.camera_margin) and, where it can,
 * of the HUD at the top (cameraKeep, FWP.camera_keep_top). The popups' dim is darker (`dim`): the enlarged HUD under a
 * popup reads as its background. Screens with a layout of their own (`custom`): the battle's weapon sheet and scanner
 * on a touch screen (ui/battle-sheet.ts, ui/battle-scanner.ts), the battle's result popups (ui/battle-results.ts).
 * Without a wide host (a desktop) nothing of this happens: the layout is the original's (in touch mode without the
 * backpack).
 *
 * Only the presentation changes: no game state, no input path (the battle's map click area is stretched like its
 * picture, and a click's world point stays what the camera shows), nothing a battle's simulation reads.
 */
import { Event, GlowFilter, Point, Rectangle, Sprite, TextFormat } from '@flash';
import { setRawText, setRawTextFit } from './controls';
import { LogFold, type LogFoldSpec } from './battle-log';
import { SCANNER } from './battle-scanner';
import { type ScreenLayout, WEAPON_SHEET } from './battle-sheet';
import { BATTLE_MESSAGE_POPUP, GRAVE_SEARCH, ONLINE_RESULT_POPUP, RESULT_POPUP } from './battle-results';

/** A rectangle in stage coordinates. */
interface Area {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** How one of the original's screens fills the margin around the stage. */
export interface ScreenDress {
  /** Keep the screen's own children inside the stage rectangle (default true). */
  clip?: boolean;
  /**
   * Children (character ids) never clipped: art of the scene that goes on beyond the window (the main map's sun, its
   * glow and its rays).
   */
  free?: number[];
  /**
   * Children (instance names) left unclipped: controls of the original placed beyond the window that the port's
   * features use, shown in the margin while the safe part of what is shown holds them whole (else clipped as before).
   */
  unclip?: string[];
  /** Children (character ids or instance names) stretched over the whole shown area: flat backgrounds, dims. */
  stretch?: Array<number | string>;
  /** The screen itself is a flat background stretched over the shown area. */
  stretchSelf?: boolean;
  /**
   * Children (character ids) cut in three: the `edge` px at each side move out to the shown edges and the column `fill`
   * px in from the side is stretched between them: a flat panel whose frame then borders the whole screen. Above and
   * below the window (a screen narrower than the window's shape) likewise: `top` [edge, row] moves the panel's top
   * `edge` px up to the shown top and stretches its row `row` px from its top between; `bottom` [edge, row, over] moves
   * its bottom `edge` px down to the shown bottom (none when 0) and stretches the row `row` px from its top between,
   * from `over` px above the panel's bottom (default: over the moved rows' place and a pixel and a half more; a frame
   * round a picture keeps its line under the picture, its last rows the stretched row's colour).
   */
  panels?: Array<{ id: number; edge: number; fill: number; top?: [number, number]; bottom?: [number, number, number?] }>;
  /**
   * A dim over the whole window with panels on it (character id; the dim a flat `color` at `alpha`): on a wide screen one
   * flat dim over the whole shown area and, instead of the child, the extension art `name` (the child without its dim,
   * tools/extract/lib/wide/dims.mjs): pieces of a translucent dim would darken a line where they overlap and leave one
   * where they meet. `edges`: the art's edge columns stretched out to the shown sides (a banner running off the window).
   */
  dims?: Array<{ id: number; name: string; color: number; alpha: number; edges?: boolean }>;
  /**
   * Children (character ids) that end `gap` px above the window's bottom (translucent panels over a scene): below the
   * window (a screen narrower than the window's shape) the extension art `name` (the child drawn anew with its own
   * continuation down, one picture: two meeting at the child's translucent edge would leave a line) is shown instead,
   * cut `gap` px above the shown bottom.
   */
  taller?: Array<{ id: number; name: string; gap: number }>;
  /**
   * Extension art: shapes of the screen's bundle (exported classes) drawn in stage coordinates beyond the window's
   * sides, put right above the child `above` or right below the child `under` (character ids; default on top of the
   * screen).
   */
  art?: Array<{ name: string; above?: number; under?: number }>;
  /**
   * The HUD's groups, in the order they are placed (a group placed next to another comes after it): moved to the
   * shown safe area's corners and enlarged while a wide host shows the screen.
   */
  hud?: HudGroup[];
  /**
   * The scene's key objects the HUD must not cover (more than the original did): children (ids, names), rectangles, or
   * a HUD group's box as the original lays it out (its name: the room it keeps for itself, kept by the groups placed
   * before it).
   */
  keep?: Array<number | string | Area>;
  /**
   * What the screen paints (children: ids, names, extension art), when less than all that is shown: the HUD stays in
   * the part of the shown safe area they cover (the main map in a portrait browser: the window, not the dark above).
   */
  paint?: Array<number | string>;
  /**
   * A black layer of this opacity over the screen's stretched dim while the HUD is laid out: the popups' dim (25 %
   * black), darker under a popup over the enlarged HUD.
   */
  dim?: number;
  /**
   * The screen's own layout, after the rest of its dressing whenever it is laid out, and every frame (the battle's
   * weapon sheet and scanner on a touch screen, the battle's results: ui/battle-sheet.ts).
   */
  custom?: ScreenLayout;
}

/**
 * A part of a screen's HUD placed and scaled as one (a sprite that gathers the screen's children, in their order):
 * at a corner of the shown safe area, or next to a group placed before it.
 */
export interface HudGroup {
  /** The group's name (its sprite is `$wide_hud_<name>`; `next` and `pair` name groups by it). */
  name: string;
  /**
   * The screen's children it gathers: instance names (every child of that name, also one the game adds later),
   * character ids, pieces of a child two groups share (its character id and the part of it that belongs here, in the
   * screen's coordinates; the child itself is then hidden), and extension art of the screen's bundle drawn with the
   * group (right above the child `above`).
   */
  parts: Array<string | number | { id: number; src: Area } | { art: string; above: number }>;
  /** The group's rectangle as the original lays it out (the screen's coordinates): what is placed and kept clear. */
  box: Area;
  /**
   * An empty corner of its box (the screen's coordinates): what the group covers there is not counted against the key
   * objects and the groups placed before it (the player's plate: under the nickname's room, right of the flags).
   */
  notch?: Area;
  /**
   * The corner of the shown safe area it goes to (t/b: top/bottom, l/r: left/right), or `tc`: the top, its own middle
   * where the original has it. With the touch controls on, a bottom corner of a `clearControls` group is right above
   * the controls' cluster on that side.
   */
  at: 'tl' | 'tr' | 'bl' | 'br' | 'tc';
  /**
   * Next to a group placed before it instead of at the corner: of these (the groups placed, and `corner`: its corner),
   * the place where it grows most (the first of equals). Below or above it (lined up on the corner's side), or left or
   * right of it (lined up with it on the corner's side, `end`: on the other).
   */
  next?: Array<'corner' | [string, 'below' | 'above' | 'left' | 'right'] | [string, 'left' | 'right', 'end']>;
  /**
   * The scale it aims at: its smallest control or text (px as the original draws it) shown at `css` CSS px, never
   * below 1 nor above `max`; then less while it would cover what it must not.
   */
  size: number;
  css: number;
  max: number;
  /**
   * The scale it aims at, at least (a tablet, where its controls measure `css` already at 1: in step with the plates
   * beside it, which grow more).
   */
  min?: number;
  /** One scale with this group (placed before it): the smaller of both. */
  pair?: string;
  /**
   * Groups placed after it that it gives room to: while the smallest control or text of one of them measures less than
   * `css` CSS px, it is laid out again a little smaller (not below `min`, default 1). The player's plate and the icons
   * under it on a small phone, the battle's timer and the team names on a narrow one.
   */
  yieldTo?: { names: string[]; css: number; min?: number };
  /** Room to the group it is placed next to and, at a bottom corner, to the touch controls (CSS px; default HUD_GAP_CSS). */
  gap?: number;
  /** Room to the groups placed before it (CSS px), when more than the default half of HUD_GAP_CSS. */
  room?: number;
  /** Hidden while the touch controls are on (the battle's backpack: the controls have their own weapon button). */
  noTouch?: boolean;
  /** Kept clear of the touch controls' clusters (FWP.touch_zones: the battle). */
  clearControls?: boolean;
  /**
   * Room to the touch controls' clusters (CSS px; default HUD_CLEAR_CSS): more for a button whose tap spends something
   * (the scanner), beyond the reach of a finger that missed a touch button (the touch slop, 14 CSS px: src/main.ts).
   */
  clear?: number;
  /**
   * Buttons of the group (instance names of the buttons, or of its children holding them) whose hit areas are their
   * art's whole rectangle (a note and a speaker whose strokes leave gaps a finger falls into) and reach beyond it
   * towards HUD_HIT_CSS square where the art is smaller (the plates' small buttons, the icons of a small phone): into
   * the room around them, clear of the other groups' taps (by the group's `gap`) and of the key objects; two buttons of
   * the group share the room between them as each needs it (the + buttons above the overview's row), and a button near
   * the shown area's edge grows into the margin first (music at the bottom left corner). Buttons whose art mostly
   * overlaps (a clip's buttons of its two states, the "Papa is here" figure over his circle) are one target; the
   * group's other buttons keep their art. The groups placed after it keep clear of these hit areas (next to it: its
   * gap from them). The music and sound clips place a new button at each toggle, which gets it at the frame it comes.
   */
  hit?: string[];
  /**
   * In step with a group placed before it: its scale at most `ratio` times that group's (the gold plate beside the
   * player's plate, which the sun or the icons under it hold smaller: the top corners about the same size).
   */
  step?: { name: string; ratio: number };
  /**
   * A text field of the group (instance name) whose lines may narrow down to `min` px, and then its height shorten
   * down to `minH` px (fewer lines), the group's box with it, before the group scales down: the battle's log beside
   * the team bars and above the scanner; its newest lines are then shown. `rule`: a line made only of this character
   * (the log's turn separator, 41 dashes: proc.add_status) is shortened to the narrowed line's width while it is
   * narrowed, one line still, not two (the text the game wrote is shown again when the field is as wide as before).
   */
  wrap?: { name: string; min: number; minH?: number; rule?: string };
  /** Faded while the battle's current worm is under it (FWP.hud_focus). */
  fade?: boolean;
  /**
   * Hidden (its place kept) while one of these screens of the original is open over the HUD's screen (their names in
   * the game's screen stack): the battle's HUD under the weapon sheet and the scanner (the touch controls' menu info
   * takes the log's corner; a tablet's sheet leaves the top of the shown area uncovered).
   */
  under?: string[];
  /**
   * How far (px as the original draws it) its art's top lies below its box's top (a text field's 2 px gutter and the
   * room above its capitals): at a top corner it goes this much higher, into the margin, its text's top in line with
   * the other groups' art (the log's first line with the wind bar).
   */
  lift?: number;
  /**
   * Text fields (character ids) in the group's buttons drawn in this colour and size (px as the original draws it)
   * while the HUD is laid out.
   */
  labels?: { chars: number[]; color: number; size: number };
  /**
   * Text fields of the group (instance names) whose text, where it is wider than the room it may take, is cut and ends
   * with an ellipsis while the HUD is laid out (the team names: fields of the original's size, a nickname of 16
   * characters, of which the group's box keeps the right part); that room is the field's part inside the room its
   * group takes, and more: a right-aligned field grows left into the room its group leaves at the top's left (a `tc`
   * group: up to the narrowest box of the groups placed after it at the top left, the log's).
   */
  fit?: string[];
  /**
   * Text fields of the group (instance names) right-aligned while the HUD is laid out, widened left to the group's box
   * (a longer text grows left): their text's right edge (the field's 2 px gutter in from its right) on the box's,
   * whatever their length (the turn timer's 1-3 digits under the wind bar's right end).
   */
  alignRight?: string[];
  /**
   * A child of the group (character id) kept beside an `alignRight` field's text while the HUD is laid out: its right
   * edge `gap` px (as the original draws it) left of the text's first glyph, whatever the text's length, never left of
   * its own place (the clock beside the turn timer's 1-3 right-aligned digits, which the original centres in their
   * field right of it).
   */
  lead?: { part: number; field: string; gap: number };
  /** A soft outline of this colour around the group's art while the HUD is laid out (a label over the scene's art). */
  outline?: number;
  /**
   * The `wrap` field is a log over the battlefield (ui/battle-log.ts): a dark backdrop behind its lines, and folded to
   * its running turn's newest lines a few seconds after it changed.
   */
  fold?: LogFoldSpec;
}

/** Room around the HUD's groups (CSS px): from the safe area's edges, between groups, around the key objects. */
const HUD_MARGIN_CSS = 8;
/**
 * Room from a safe edge that lies in from the shown area's (a camera cutout's band, a system bar: CSS px): the band keeps
 * the HUD off the screen's edge already, and on a small phone the room it takes is short beside the scene.
 */
const HUD_INSET_MARGIN_CSS = 4;
const HUD_GAP_CSS = 6;
const HUD_CLEAR_CSS = 4;
/** The touch target the `hit` buttons' hit areas grow towards (CSS px, each side). */
const HUD_HIT_CSS = 48;
/** How far in (CSS px) the battle's groups follow the touch controls' clusters' outer edges instead of the margin. */
const HUD_ALIGN_CSS = 32;
/** The opacity of a `fade` group over the battle's current worm. */
const HUD_FADE = 0.4;
/** A `yieldTo` group's step (of scale), and the steps it gives before it is taken back when its groups did not grow. */
const HUD_YIELD_STEP = 0.02;
const HUD_YIELD_TRIES = 4;
/** How much more a group (with its pair) must grow at a later one of its places to be moved there from an earlier one. */
const HUD_SPOT_GAIN = 0.1;

/** Panels of the screens (character ids of the interf bundle). */
const PANEL = 270; // the tab screens' frame: the beige tab strip and the pale body in a green border (shape 269)
const PANEL_SKILLS = 410; // the skills screen's frame with its inner dividers
const FRAME_TOP = 587; // the tab strip and the border around a picture (roulette, replays, clans; shape 586)
const DIM = 1058; // menu_bg: the dim under the popups
const GREY = 202; // the flat grey under the tab screens
const SUN: Area = { x: 355, y: 50, width: 55, height: 56 }; // the main map's sun (1166 at 382.55, 77.85; 55 px across)

/**
 * The main map's HUD (_mainframe). Shape 1351 draws both the level box of the player's plate and the frame of the gold
 * and diamonds plate: the player's plate shows its piece of it, the gold plate is extension art (the same piece with the
 * rounded end the window's edge cut off, one image).
 */
const MAINFRAME_HUD: HudGroup[] = [
  {
    // The player's plate: the avatar, the level box, the nickname plate (ui/profile-badge.ts, added at the first
    // render; its widest nickname ends at x 284), the experience and tiredness bars with their hover buttons, the flags.
    name: 'plate',
    parts: [1344, 372, 1345, 'tired_bar', 1348, 1349, 'exp_bar', 1350, { id: 1351, src: { x: 50, y: 4, width: 65, height: 37 } }, 'avatar_container', 'my_level', 'my_exp', 'init_tired', 'my_nickname'],
    box: { x: 11, y: 4, width: 274, height: 84 },
    // The nickname plate (y 5-34) reaches x 284, the bars and flags below it x 235: the tower's roof may come there.
    notch: { x: 236, y: 35, width: 49, height: 53 },
    // Not at the cost of Papa John, the quests, music and sound under it (a 320 CSS px tall phone), while that gives
    // them room (not where the arsenal holds them: a camera cutout at the left), and never below 1.4.
    yieldTo: { names: ['column', 'music'], css: 48, min: 1.4 },
    at: 'tl',
    size: 24,
    css: 48,
    max: 2,
  },
  {
    // Gold and diamonds with their + buttons, "?", the overview and the settings: its own scale (the tower may stop the
    // player's plate sooner on a narrow screen), the small buttons' hit areas beyond their art.
    name: 'balance',
    parts: [{ art: 'wide_mainframe_plate_r', above: 1351 }, 180, 178, 'gold', 'diam', 'donate', 'view_tutorial_mf', 'init_info', 'init_settings'],
    // (to the rounded end's outline, 814.8: extension art drawn into 1351, tools/extract/lib/wide/map.mjs)
    box: { x: 581, y: 4, width: 233.8, height: 84 },
    at: 'tr',
    size: 24,
    css: 48,
    max: 2,
    // Not much larger than the player's plate (a 4:3 tablet's sun, a small phone's icons hold it smaller).
    step: { name: 'plate', ratio: 1.1 },
    hit: ['donate', 'view_tutorial_mf', 'init_info', 'init_settings'],
    // The overview's and the settings' labels (Arial 15 bold, dark green on the dark plate; "параметри" from edge to
    // edge): a step smaller, light green.
    labels: { chars: [1356, 1353], color: 0xd4f0a0, size: 13 },
  },
  // Papa John (and his "here" state) and the quests' scroll, under the plate. The icon groups grow on a tablet too
  // (`min`), where their 40 px art measures 48 CSS px already: in step with the plates.
  // On a 320 CSS px tall phone it gives music and sound under it a little room too (down to 42.6 CSS px). The icons'
  // hit areas 48 CSS px where their art is less (the quests beside a camera cutout).
  { name: 'column', parts: ['init_papa_john', 'init_papa_john_active', 'announcement_task'], box: { x: 8, y: 90, width: 52, height: 109 }, at: 'tl', next: [['plate', 'below']], size: 40, css: 48, max: 2.2, min: 1.3, yieldTo: { names: ['music'], css: 40, min: 2 }, hit: ['init_papa_john', 'init_papa_john_active', 'announcement_task'] },
  // Saving the last battle's replay (the port's; beside the quests beyond the window in the original's layout), when
  // there is one: right of the quests, else under them.
  { name: 'replay', parts: ['get_replay_clip'], box: { x: -76, y: 155, width: 59, height: 53 }, at: 'tl', next: [['column', 'right', 'end'], ['column', 'below']], size: 40, css: 48, max: 2.2, min: 1.3, hit: ['get_replay_clip'] },
  // The auction (the port's; right of the gold plate beyond the window, under the hit areas of its buttons) and the
  // achievements' announcement.
  { name: 'auction', parts: ['click_auc'], box: { x: 871, y: 91, width: 50, height: 48 }, at: 'tr', next: [['balance', 'below']], size: 40, css: 48, max: 2.2, min: 1.3, hit: ['click_auc'] },
  { name: 'achiev', parts: ['announcement_achiev'], box: { x: 751, y: 92, width: 51, height: 43 }, at: 'tr', next: [['auction', 'left']], size: 40, css: 48, max: 2.2, min: 1.3, hit: ['announcement_achiev'] },
  // Music at the bottom left, sound beside it, or above it where the arsenal leaves no room beside (a narrow phone),
  // or higher, under the quests, where the arsenal's top is beside it too (a camera cutout at the left moves them in
  // over the arsenal); where the arsenal fills the bottom left corner (a 4:3 tablet), both under the quests. Stacked
  // under the quests on a small phone, 8 CSS px apart as the touch controls' buttons are, their hit areas too (a thumb
  // aimed at the scroll's lower edge must not toggle the sound): there, where their art measures about 42 CSS px, hit
  // areas of 48. On a tablet in step with the icons above them (about 62 CSS px: 1.3 times a phone's).
  { name: 'music', parts: ['music'], box: { x: 11, y: 557, width: 29, height: 30 }, at: 'bl', next: ['corner', ['column', 'below']], size: 28, css: 48, max: 3, min: 1.7, gap: 8, room: 8, hit: ['music'] },
  { name: 'sound', parts: ['sound'], box: { x: 58, y: 557, width: 41, height: 33 }, at: 'bl', next: [['music', 'right'], ['music', 'above'], ['column', 'below'], ['music', 'right', 'end']], size: 28, css: 48, max: 3, min: 1.7, pair: 'music', gap: 8, room: 8, hit: ['sound'] },
  // The version label (right-aligned text field 1379, dark grey): a light outline over the fields and stones of the
  // extension art it moves onto. Its box is its text (the field's 2 px gutter in from its right, its capitals' top to
  // their baseline): at the corner as music is at the other.
  { name: 'version', parts: [1379], box: { x: 596, y: 587, width: 210.2, height: 8.7 }, at: 'br', size: 12, css: 12, max: 1.6, outline: 0xffffff },
  // The chat (the port's: ui/chat-panel.ts, under the gold plate's buttons in the window) at the bottom right above
  // the version label, where nothing of the scene or the HUD is: a thumb's reach, a 48 CSS px target.
  { name: 'chat', parts: ['chat_open'], box: { x: 752, y: 128, width: 44, height: 44 }, at: 'br', next: [['version', 'above'], 'corner'], size: 40, css: 48, max: 2.2, min: 1.3, hit: ['chat_open'] },
];

/**
 * The battle's HUD (menu_game): the wind at the top right, the clock and the turn timer under it, the team names and
 * health bars at the top's middle, the log at the top left (its lines narrowed beside the team bars), surrender under
 * the timer (with the touch controls: above their weapon button, in line with them), the backpack and the scanner left
 * of it (or left of the clock, under the wind), all clear of the touch controls and faded over the current worm. On a
 * tablet, where their controls and text measure what they aim at already, they grow too (`min`): in step with the team
 * bars between them. All of it is hidden under the weapon sheet and the scanner (`under`: their frames fill the shown
 * area on a phone, not its whole height on a tablet), as the original's covered its window.
 */
const BATTLE_MENUS = ['weapon_sheet', 'scaner'];
const BATTLE_HUD: HudGroup[] = [
  // wind_container gets its bar only after the screen is on the stage: the box is the original's. At most 1.5, down to
  // the original's size while the team names measure less than 12 CSS px: the wind bar is wide (a gauge, not a
  // button), the team names need the room on a narrow phone.
  { name: 'wind', parts: ['wind_container'], box: { x: 614, y: 15, width: 181, height: 17 }, at: 'tr', size: 16, css: 20, max: 1.5, min: 1.3, yieldTo: { names: ['teams'], css: 12, min: 1 }, under: BATTLE_MENUS, clearControls: true, fade: true },
  // The clock and the turn timer's digits (16 px) under it, the digits right-aligned in their field (centred in the
  // original: 1-3 digits) and their right edge (the field's 2 px gutter in) in line with the wind bar's right end:
  // their own scale, the digits 16 CSS px and more on a phone.
  { name: 'timer', parts: [851, 'timer'], box: { x: 726, y: 42, width: 62.85, height: 35 }, at: 'tr', next: [['wind', 'below']], size: 16, css: 16, max: 2.2, min: 1.3, alignRight: ['timer'], lead: { part: 851, field: 'timer', gap: 3 }, under: BATTLE_MENUS, clearControls: true, fade: true, outline: 0x000000 },
  // The team names (right-aligned fields; the box keeps 100 px of a name, 13 characters and so: a longer nickname
  // grows left into the room before the log, then ends with an ellipsis, not cut at the flag) and the flags and health
  // bars drawn in bitmap_container; the first team's bar is kept apart from the wind bar, whose colours are alike.
  { name: 'teams', parts: ['team1_name', 'team2_name', 'team3_name', 'team4_name', 'bitmap_container'], box: { x: 302, y: 13, width: 259, height: 90 }, at: 'tc', size: 11, css: 14, max: 2, min: 1.3, room: 14, under: BATTLE_MENUS, clearControls: true, fade: true, fit: ['team1_name', 'team2_name', 'team3_name', 'team4_name'], outline: 0x000000 },
  { name: 'surrender', parts: ['cancel_fight'], box: { x: 761, y: 555, width: 35, height: 35 }, at: 'tr', next: [['timer', 'below']], size: 34, css: 48, max: 2.6, min: 1.3, under: BATTLE_MENUS, clearControls: true, fade: true },
  { name: 'backpack', parts: ['open_wpsheet'], box: { x: 763, y: 510, width: 32, height: 35 }, at: 'tr', next: [['surrender', 'left']], size: 34, css: 48, max: 2.6, min: 1.3, pair: 'surrender', noTouch: true, under: BATTLE_MENUS, clearControls: true, fade: true },
  // Left of the backpack or surrender; where the touch controls leave it less room there (a phone of 360 CSS px or less:
  // the bounce button's column above the fuse), left of the clock under the wind bar, else at the left right above the
  // d-pad. More room than the default from surrender and the clock (a miss of the scanner should not ask whether to
  // surrender: the touch confirmation, src/main.ts) and from the touch controls (a tap spends a scanner: out of the
  // touch slop's reach of a finger that missed a touch button).
  { name: 'scanner', parts: ['open_scaner'], box: { x: 754, y: 462, width: 50, height: 35 }, at: 'bl', next: [['backpack', 'left'], ['surrender', 'left'], ['timer', 'left', 'end'], 'corner'], size: 34, css: 48, max: 2.6, min: 1.3, gap: 12, clear: 16, under: BATTLE_MENUS, clearControls: true, fade: true },
  // Its lines narrowed beside the team bars, fewer of them above the scanner at the left (8 CSS px from it); its text
  // in line with the other groups' art: its box starts at the text (the field's 2 px gutter in from its left), its
  // first line's capitals in line with the wind bar's top (the gutter and the room above the capitals of its Arial 10
  // above them). Over the battlefield: a dark backdrop behind its lines, folded to the running turn's newest two a few
  // seconds after it changed (ui/battle-log.ts).
  { name: 'log', parts: ['status_text'], box: { x: 7, y: 5, width: 239, height: 100 }, at: 'tl', size: 10, css: 14, max: 2.2, min: 1.3, room: 8, lift: 4, under: BATTLE_MENUS, clearControls: true, fade: true, wrap: { name: 'status_text', min: 114, minH: 50, rule: '-' }, fold: { lines: 2, seconds: 4, backdrop: { color: 0x000000, alpha: 0.4 } } },
];

/**
 * How the tab screens' panels are cut: the frame 12 px wide at the sides (the column 14 px in stretched); at the top
 * the pale rim, the green line with its soft lower edge and the tab strip's first rows (11 px: the line keeps its width)
 * and a row of the tab strip (20 px down) stretched; at the bottom the green line and the rim (10 px) and a row of the
 * body (15 px up), or, under a framed picture, a row of the frame's pale rim (its last 5 px, below the green line under
 * the picture) from 3 px above the frame's bottom (over its last texels, which the screen's filtering fades) to the
 * shown bottom: the frame's pale outer border grows down, as its tab strip grows up to the top.
 */
const BODY = { edge: 12, fill: 14, top: [11, 20] as [number, number], bottom: [10, 585] as [number, number] };
const FRAMED = { edge: 12, fill: 14, top: [11, 20] as [number, number], bottom: [0, 596, 3] as [number, number, number] };

/**
 * The bar's room beyond the window's left side (under the room, above the flat grey), beyond its right side (over the
 * room's last column: see tools/extract/lib/wide/common.mjs RIGHT_OVER), above and below it (over the room's edge row:
 * TOP_OVER), the art above and below lying over the right side's.
 */
const ROOM = 775;
const ROOM_ART = [
  { name: 'wide_room_l', above: GREY },
  { name: 'wide_room_t', above: ROOM },
  { name: 'wide_room_b', above: ROOM },
  { name: 'wide_room_r', above: ROOM },
];

/**
 * The bar's translucent panels (the teams' table, the task, the game mode sign: shape 776) end 4 px above the window's
 * bottom; below the window they go on down to the shown bottom, running off the screen (tools/extract/lib/wide/bar.mjs).
 */
const ROOM_PANELS = [{ id: 776, name: 'wide_room_panels', gap: 0 }];

/** The screens of the original (their linkage class) and how they are dressed. */
export const SCREEN_DRESS: Readonly<Record<string, ScreenDress>> = {
  // interf: the main map, the tab screens, the popups' dim, the battle's screen
  // The auction (click_auc, right of the gold / diamonds plate), saving the last battle's replay (get_replay_clip, left
  // of the avatar) and the wormpedia (above the window) lie beyond the original's window; the port enabled them.
  // The map's extension art beyond its sides lies right above its clouds (1170), under the controls shown in the margin
  // and the HUD, and 3 px over the window's edge, where it shows the window's own pixels (the map's and the clouds'
  // images end in a texel the screen's filtering fades to transparent). Above and below the window (a screen narrower
  // than the window's shape): the sky lies under the map (reaching under its edge; the map leaves its top 0.4 px
  // uncovered), the land below it right above the map (from 3 px inside the window: the map's last row of texels is
  // only partly covered), under the sun's glow (1167) and its rays (1169), which go on up over the sky unclipped, as do
  // the clouds (1170: the clouds above the window are drawn into it). The top bar's right plate (1351) has its rounded
  // end drawn into it and shows it unclipped; the HUD shows its level box as a piece and the plate with the end as
  // extension art of its own (the group "balance").
  _mainframe: {
    art: [
      { name: 'wide_mainframe_s', under: 1165 },
      { name: 'wide_mainframe_b', above: 1165 },
      { name: 'wide_mainframe_l', above: 1170 },
      { name: 'wide_mainframe_r', above: 1170 },
    ],
    free: [1167, 1169, 1170, 1351],
    unclip: ['click_auc', 'get_replay_clip', 'wormpedia'],
    // The bar's tower, the shop, the arsenal's quarter, the small button at the tower's foot, the sun's disc (sprite
    // 1167: its glow reaches 100 px beyond it, over the sky the player's plate may take on a 16:9 phone).
    keep: ['init_fight', 'init_shop', 'init_skills_main', 1335, SUN],
    // The map (the window) and its extension art: in a portrait browser the window and the 40 px of sky and land the
    // art paints above and below it, not the dark beyond.
    paint: [1165, 'wide_mainframe_s', 'wide_mainframe_b', 'wide_mainframe_l', 'wide_mainframe_r'],
    hud: MAINFRAME_HUD,
  },
  // The popups' dim: darker under a popup over the enlarged HUD (45 % in all).
  menu_bg: { stretch: [DIM], dim: 0.27 },
  // (the tutorial's dims and spotlights: src/game/ui/wide-popups.ts, which shows it larger on a phone)
  menu_weapon_sheet: { stretch: [143], custom: WEAPON_SHEET },
  menu_scaner: { clip: false, custom: SCANNER },
  // The battle's results: larger on a phone (ui/battle-results.ts).
  ...Object.fromEntries(
    ['menu_end_game_win', 'menu_end_game_lose', 'menu_end_game_new_lvl', 'menu_end_game_surprise', 'menu_what_opened', 'menu_surprise'].map((n) => [n, { clip: false, custom: RESULT_POPUP }]),
  ),
  menu_grave_search: { clip: false, custom: GRAVE_SEARCH },
  // (its messages of the battle's results only: nothing to search, nothing opened)
  menu_error: { clip: false, custom: BATTLE_MESSAGE_POPUP },
  menu_question: { clip: false, custom: ONLINE_RESULT_POPUP },
  // The team names, flags and health bars (the HUD's `teams`) keep the top's middle from the groups placed before them
  // (the wind, the timer); those after them keep clear of their art.
  menu_game: { clip: false, stretch: ['inv_button'], keep: ['teams'], hud: BATTLE_HUD },
  // The bar's and the shop's left art lies right above the flat grey, under the room / stall: it reaches a few px into
  // the window, where the picture covers it but for the picture's anti-aliased edge (else a dark line along the edge).
  // Their right art lies over the picture: the room's image ends in a texel the screen's filtering darkens (see
  // tools/extract/lib/wide/common.mjs RIGHT_OVER), the bar's art draws its last column anew; the stall ends just short
  // of the window's right edge with a dark fringe, the shop's art draws the stall's last 1.3 px anew (wide/shop.mjs).
  menu_fight: { stretch: [GREY], art: ROOM_ART, taller: ROOM_PANELS },
  menu_just_play: { stretch: [GREY], art: ROOM_ART, taller: ROOM_PANELS },
  menu_shop: {
    stretch: [GREY],
    art: [
      { name: 'wide_shop_l', above: GREY },
      { name: 'wide_shop_t', above: 455 },
      { name: 'wide_shop_b', above: 455 },
      { name: 'wide_shop_r', above: 455 },
    ],
  },
  // Above and below the window the panels' tab strip grows up to the shown top and their body down to the shown bottom,
  // their frame bordering the screen; under a framed picture the frame keeps its line and its pale outer border grows
  // down to the shown bottom.
  menu_friends: { stretch: [GREY], art: [{ name: 'wide_picture_l', above: 867 }, { name: 'wide_picture_r', above: 867 }], panels: [{ id: FRAME_TOP, ...FRAMED }] },
  menu_roul: { stretch: [GREY], art: [{ name: 'wide_picture_l', above: 585 }, { name: 'wide_picture_r', above: 585 }], panels: [{ id: FRAME_TOP, ...FRAMED }] },
  menu_replay: { stretch: [GREY], art: [{ name: 'wide_picture_l', above: 649 }, { name: 'wide_picture_r', above: 649 }], panels: [{ id: FRAME_TOP, ...FRAMED }] },
  menu_skills: { stretch: [GREY], panels: [{ id: PANEL_SKILLS, ...BODY }] },
  menu_arsenal: { panels: [{ id: PANEL, ...BODY }] },
  menu_crafting: { panels: [{ id: PANEL, ...BODY }] },
  menu_tasks: { panels: [{ id: PANEL, ...BODY }] },
  menu_achiev: { stretch: [GREY], panels: [{ id: PANEL, ...BODY }] },
  menu_assault: { stretch: [GREY], panels: [{ id: PANEL, ...BODY }] },
  menu_top: { stretch: [GREY], panels: [{ id: PANEL, ...BODY }] },
  menu_user_info: { stretch: [GREY] },
  // fwp: the loading splash (also the sign-in screen's background) and the waiting / error splashes; the splash's art
  // lies under its picture (190) on the left and over it on the right, like the bar's
  splash_loading: {
    art: [
      { name: 'wide_splash_l', under: 190 },
      { name: 'wide_splash_t', above: 190 },
      { name: 'wide_splash_b', above: 190 },
      { name: 'wide_splash_r', above: 190 },
    ],
  },
  _splash_waiting: { dims: [{ id: 7, name: 'wide_dim_waiting', color: 0, alpha: 204 / 255, edges: true }] },
  _splash_error: { dims: [{ id: 20, name: 'wide_dim_error', color: 0, alpha: 127 / 255 }], stretch: ['inv_btn'] },
  _splash_ban: { dims: [{ id: 40, name: 'wide_dim_ban', color: 0, alpha: 83 / 255 }] },
  _splash_big_error: { dims: [{ id: 36, name: 'wide_dim_big_error', color: 0, alpha: 86 / 255 }] },
  _splash_system_error: { dims: [{ id: 11, name: 'wide_dim_system_error', color: 0, alpha: 81 / 255 }] },
  // world: the battle's sky (a vertical gradient)
  ...Object.fromEntries(
    ['wormville', 'traver', 'sheovar', 'rolitrad', 'molivand', 'forbidden1', 'forbidden2', 'cave_mission'].map((n) => [`background_${n}`, { clip: false, stretchSelf: true }]),
  ),
};

/** Instance names of the parts the dressing adds (never names of the original). */
const PART = '$wide_';

interface Dressed {
  root: any;
  dress: ScreenDress;
  /** Stretched children with their transform as the original placed it. */
  stretched: Array<{ obj: any; x: number; y: number; sx: number; sy: number; b: Area }>;
  /** Parts laid out per shown area. */
  parts: Array<(view: Area, wide: boolean) => void>;
  /** The scene's key objects the HUD keeps clear of (the screen's coordinates, as the original placed them; `own`: a group's). */
  keep: Array<{ r: Area; own?: string }>;
  /** The HUD's groups, gathered at the first layout with a wide host (null: not yet, or the screen has none). */
  hud: HudBuilt[] | null;
  /** Which of its groups showed something at the HUD's last layout (a change lays it out again). */
  shown: string;
}

/** A HUD group on the screen: its sprite and the children it gathered. */
interface HudBuilt {
  spec: HudGroup;
  sprite: any;
  /** Gathered children and their $designClip as dressed (null: left to the `unclip` rule). */
  members: Array<{ obj: any; clip: boolean | null }>;
  /** The `hit` buttons with a hit area of their own now: it and their hit state as the original has it. */
  hits: Map<any, { own: any; orig: any; on: boolean }>;
  /** The buttons it showed at the HUD's last layout (one its clip places later is given its hit area then). */
  known: WeakSet<any>;
  /**
   * The group's other children under those hit areas (the gold and diamonds amounts over the + buttons' room: no
   * buttons, no mouse listeners) and what those buttons' states hold (a label), kept from taking the taps there, with
   * their mouseEnabled / mouseChildren as they were.
   */
  mutes: Map<any, { enabled: boolean; children: boolean | undefined }>;
  /**
   * The `wrap` field: its size and word wrap as the original has them, whether narrowed or shortened now, the text last
   * shown and the text the game wrote behind it (its `rule` lines as long as the game made them; null: none seen).
   */
  wrap: { field: any; width: number; height: number; wordWrap: boolean; narrow: boolean; text: string; src: string | null; rule: string | null } | null;
  /** The `labels` fields with their size and colour as the original has them (null: not looked up yet). */
  labels: Array<{ field: any; size: number; color: number }> | null;
  /** The `alignRight` fields with their alignment, place and width as the original has them (null: not looked up yet). */
  aligns: Array<{ field: any; align: string; x: number; width: number }> | null;
  /** The `lead` child: its x and its right edge as the original has them, the text it was last placed beside. */
  lead: { obj: any; field: any; x: number; right: number; text: string | null } | null;
  /**
   * The `fit` fields: the text the game wrote, the text shown (null: not cut yet), their place and width as the
   * original has them and how far left of them their text may reach now (px of the group's coordinates; less than 0:
   * from inside them); null: not looked up yet.
   */
  fits: Array<{ field: any; full: string; shown: string | null; x: number; width: number; grow: number }> | null;
  /** How far left (the group's coordinates) its `fit` fields may reach (Infinity: not beyond their own place). */
  fitLeft: number;
  /** The rectangle its art may take (its rect, reaching left as far as its `fit` fields may grow; null: not placed). */
  reach: Area | null;
  /** An `under` group hidden now by a screen over it. */
  covered: boolean;
  /** Where it is placed now (the screen's coordinates; null: hidden, or no wide host). */
  rect: Area | null;
  /** A `fit` group's art as laid out last (HudPlaced.solid; null: none): the team names' text changing it lays the HUD out again. */
  solid: Area | null;
  /** The `fold` log's backdrop and folding (null: not laid out yet). */
  fold: LogFold | null;
}

/** Where a HUD group is placed now (the screen's coordinates), its scale and its box's size (a `wrap` cuts it). */
interface HudPlaced {
  rect: Area;
  k: number;
  w: number;
  h: number;
  /**
   * What the groups placed after it keep clear of, where less than its rectangle: a `fit` group's art (the team names'
   * right-aligned fields keep room for a nickname of 16 characters; the log beside them narrows to their text, not to
   * the fields).
   */
  solid?: Area;
  /** Its `hit` buttons' hit areas (the screen's coordinates), and its rectangle with them: where its taps land. */
  hits?: Map<any, Area>;
  taps?: Area;
}

/** A place of a group next to another (null: its corner). */
type HudSpot = { name: string; side: 'below' | 'above' | 'left' | 'right'; end: boolean } | null;

/** The area two rectangles share (0: none). */
function overlapArea(a: Area, b: Area): number {
  const w = Math.min(a.x + a.width, b.x + b.width) - Math.max(a.x, b.x);
  const h = Math.min(a.y + a.height, b.y + b.height) - Math.max(a.y, b.y);
  return w > 0 && h > 0 ? w * h : 0;
}

/** `r` grown by `d` on every side. */
function inflate(r: Area, d: number): Area {
  return { x: r.x - d, y: r.y - d, width: r.width + 2 * d, height: r.height + 2 * d };
}

/** The rectangle two rectangles share, or null. */
function intersect(a: Area, b: Area): Area | null {
  const x = Math.max(a.x, b.x);
  const y = Math.max(a.y, b.y);
  const w = Math.min(a.x + a.width, b.x + b.width) - x;
  const h = Math.min(a.y + a.height, b.y + b.height) - y;
  return w > 0.5 && h > 0.5 ? { x, y, width: w, height: h } : null;
}

/** The smallest rectangle holding both. */
function union(a: Area, b: Area): Area {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  return { x, y, width: Math.max(a.x + a.width, b.x + b.width) - x, height: Math.max(a.y + a.height, b.y + b.height) - y };
}

/** Bounds of `o` in the coordinates of `space` (default its parent's), as placed now. */
function boundsIn(o: any, space: any = o.parent): Area {
  const b = o.getBounds(space);
  return { x: b.x, y: b.y, width: b.width, height: b.height };
}

/**
 * Bounds of a stretched child in its parent's coordinates: its art's, or, for a button with no art of its own (the
 * battle's map click area: an invisible button, only a hit state), its hit area's.
 */
function stretchBounds(o: any): Area {
  const b = boundsIn(o);
  const hit = b.width > 0 && b.height > 0 ? null : o.hitTestState;
  if (!hit || typeof hit.getBounds !== 'function') return b;
  const h = hit.getBounds(hit);
  const m = o.transform.matrix;
  const xs = [h.x, h.x + h.width].map((x) => [m.a * x + m.c * h.y + m.tx, m.a * x + m.c * (h.y + h.height) + m.tx]).flat();
  const ys = [h.x, h.x + h.width].map((x) => [m.b * x + m.d * h.y + m.ty, m.b * x + m.d * (h.y + h.height) + m.ty]).flat();
  const x = Math.min(...xs);
  const y = Math.min(...ys);
  return { x, y, width: Math.max(...xs) - x, height: Math.max(...ys) - y };
}

/** A new instance of the character `o` shows (the same symbol of the same bundle), or null. */
function cloneOf(o: any): any {
  const sym = o.$sym;
  if (!sym || typeof sym.instantiate !== 'function' || !o.$charId) return null;
  const c = sym.instantiate(o.$charId);
  if (!c) return null;
  if (typeof c.gotoAndStop === 'function') c.gotoAndStop(1);
  return c;
}

/**
 * A sprite showing the part `src` (in `o`'s parent's coordinates) of a copy of `o`, stretched over `dst`: the pieces
 * of the edge columns and the cut panels. Not interactive.
 */
function piece(o: any, name: string): { sprite: any; place: (src: Area, dst: Area | null) => void } | null {
  const copy = cloneOf(o);
  if (!copy) return null;
  const s = new Sprite();
  s.name = PART + name;
  s.mouseEnabled = false;
  s.mouseChildren = false;
  const m = o.transform.matrix;
  copy.transform.matrix = m;
  s.addChild(copy);
  return {
    sprite: s,
    place(src: Area, dst: Area | null) {
      if (!dst || !(dst.width > 0.01 && dst.height > 0.01) || !(src.width > 0 && src.height > 0)) {
        s.visible = false;
        s.x = -10000; // never at a place the game looks up a child by its position
        return;
      }
      s.visible = true;
      s.scrollRect = new Rectangle(src.x, src.y, src.width, src.height);
      s.scaleX = dst.width / src.width;
      s.scaleY = dst.height / src.height;
      s.x = dst.x;
      s.y = dst.y;
    },
  };
}

export class WideScreen {
  private readonly dressed: Dressed[] = [];

  /**
   * @param fwp the game's root (screens are put on the stage below it)
   * @param stage the stage (its visibleRect and RESIZE)
   * @param art the bundle containers that hold the extension art (interf, fwp)
   * @param table how each screen is dressed (default SCREEN_DRESS)
   */
  constructor(
    private readonly fwp: any,
    private readonly stage: any,
    private readonly art: () => Array<{ has(n: string): boolean; get_object(n: string): any }>,
    private readonly table: Readonly<Record<string, ScreenDress>> = SCREEN_DRESS,
  ) {
    fwp.addEventListener(Event.ADDED, this.onAdded);
    fwp.addEventListener(Event.EXIT_FRAME, this.onFrame);
    stage.addEventListener(Event.RESIZE, this.onResize);
  }

  destroy(): void {
    this.fwp.removeEventListener(Event.ADDED, this.onAdded);
    this.fwp.removeEventListener(Event.EXIT_FRAME, this.onFrame);
    this.stage.removeEventListener(Event.RESIZE, this.onResize);
    this.dressed.length = 0;
  }

  /** The shown part of the stage and whether it is more than the stage. */
  view(): { area: Area; wide: boolean } {
    const r = this.stage.visibleRect as Rectangle;
    const sw = this.stage.stageWidth;
    const sh = this.stage.stageHeight;
    const area = { x: r.x, y: r.y, width: r.width, height: r.height };
    return { area, wide: r.x < -0.01 || r.y < -0.01 || r.x + r.width > sw + 0.01 || r.y + r.height > sh + 0.01 };
  }

  /** The screens dressed and still on the stage (tests, diagnostics). */
  get screens(): any[] {
    return this.dressed.filter((d) => d.root.stage).map((d) => d.root);
  }

  /** Lays out every dressed screen again (the touch controls came or went: FWP.touch_zones). */
  refresh(): void {
    this.onResize();
  }

  /**
   * What the battle camera keeps the current worm clear of (FWP.camera_keep_top): the shown battle HUD groups at the
   * top (the log's lines shown, the team names and bars, the wind, the timer), in stage coordinates; none without the
   * wide HUD.
   */
  cameraKeep(): Area[] {
    const out: Area[] = [];
    if (!this.hudOn()) return out;
    for (const d of this.dressed) {
      if (!d.hud || !d.root.stage) continue;
      for (const g of d.hud) {
        if (!g.spec.fade || g.spec.at[0] !== 't' || !g.rect || g.covered || !g.sprite.visible) continue;
        const r = WideScreen.shownArea(d, g);
        if (!r) continue;
        const p = d.root.localToGlobal(new Point(r.x, r.y));
        out.push({ x: p.x, y: p.y, width: r.width, height: r.height });
      }
    }
    return out;
  }

  /**
   * What the HUD group `name` of a screen on the stage shows now (stage coordinates: shownArea), or null: not shown, or
   * not laid out (no wide host). The touch controls' hint goes under the battle's team bars (src/main.ts).
   */
  hudRect(name: string): Area | null {
    for (const d of this.dressed) {
      const g = d.hud && d.root.stage ? d.hud.find((h) => h.spec.name === name) : undefined;
      const r = g && g.sprite.visible ? WideScreen.shownArea(d, g) : null;
      if (!r) continue;
      const p = d.root.localToGlobal(new Point(r.x, r.y));
      return { x: p.x, y: p.y, width: r.width, height: r.height };
    }
    return null;
  }

  private readonly onAdded = (e: any): void => {
    const t = e.target;
    if (!t) return;
    if (typeof t.name === 'string' && t.name.startsWith(PART)) return;
    // A child the game adds to a screen later that belongs to a HUD group (the nickname plate at the first render).
    const host = t.parent ? this.dressed.find((d) => d.root === t.parent && d.hud) : undefined;
    if (host && this.gatherLate(host, t)) return;
    if (this.visit(t)) return;
    // A screen inside a container of the port's own (the sign-in screen holds the loading splash).
    const n = typeof t.numChildren === 'number' ? t.numChildren : 0;
    if (n > 0 && n <= 8 && !t.$className) for (let i = 0; i < n; i++) this.visit(t.getChildAt(i));
  };

  /**
   * Every screen dressed so far: a screen the game keeps and shows again (the waiting and error splashes, the loading
   * splash) still holds its parts, so it is laid out again, never dressed twice (a second dim and art over the first:
   * the waiting splash's window went black between margins that were not).
   */
  private readonly dressings = new WeakMap<any, Dressed>();

  /** Dresses `root` when it is one of the original's screens (once); true when it is one. */
  private visit(root: any): boolean {
    const dress = root && root.$className ? this.table[root.$className] : undefined;
    if (!dress) return false;
    if (this.dressed.some((d) => d.root === root)) return true;
    // Screens that left the stage are dropped from the list (most are built anew each time they open).
    for (let i = this.dressed.length - 1; i >= 0; i--) if (!this.dressed[i].root.stage) this.dressed.splice(i, 1);
    let d = this.dressings.get(root);
    if (!d || d.dress !== dress) {
      d = this.dress(root, dress);
      this.dressings.set(root, d);
    }
    this.dressed.push(d);
    this.layout(d);
    return true;
  }

  private readonly onResize = (): void => {
    for (const d of this.dressed) if (d.root.stage) this.layout(d);
  };

  private findChild(root: any, key: number | string): any {
    for (let i = 0; i < root.numChildren; i++) {
      const c = root.getChildAt(i);
      if (typeof key === 'number' ? c.$charId === key : c.name === key) return c;
    }
    return null;
  }

  private dress(root: any, dress: ScreenDress): Dressed {
    const d: Dressed = { root, dress, stretched: [], parts: [], keep: [], hud: null, shown: '' };
    for (const k of dress.keep ?? []) {
      const group = typeof k === 'string' ? dress.hud?.find((g) => g.name === k) : undefined;
      if (group) d.keep.push({ r: group.box, own: group.name });
      else if (typeof k === 'object') d.keep.push({ r: k });
      else {
        const c = this.findChild(root, k);
        if (c) d.keep.push({ r: boundsIn(c) });
      }
    }
    const stretched = new Set<any>();
    for (const key of dress.stretch ?? []) {
      const c = this.findChild(root, key);
      if (!c) continue;
      stretched.add(c);
      d.stretched.push({ obj: c, x: c.x, y: c.y, sx: c.scaleX, sy: c.scaleY, b: stretchBounds(c) });
    }
    if (dress.clip !== false) {
      const keep = new Set(dress.unclip ?? []);
      const free = new Set(dress.free ?? []);
      for (let i = 0; i < root.numChildren; i++) {
        const c = root.getChildAt(i);
        if (stretched.has(c) || free.has(c.$charId)) continue;
        c.$designClip = true;
        if (!keep.has(c.name)) continue;
        // A control beyond the window: on a wide screen shown (and tapped) only while the safe part of what is shown
        // holds all of it; without the wide view as before (beyond the letterboxed window, not clipped).
        const b = boundsIn(c);
        d.parts.push((_v, wide) => {
          const r = this.stage.safeRect as Rectangle;
          c.$designClip = wide && !(b.x >= r.x && b.y >= r.y && b.x + b.width <= r.x + r.width && b.y + b.height <= r.y + r.height);
        });
      }
    }
    if (dress.stretchSelf) d.stretched.push({ obj: root, x: root.x, y: root.y, sx: root.scaleX, sy: root.scaleY, b: boundsIn(root) });
    // A darker dim over the shown area while the HUD is laid out (not clipped, never a target).
    if (dress.dim) {
      const dim = new Sprite();
      dim.name = PART + 'dim';
      dim.mouseEnabled = false;
      dim.mouseChildren = false;
      root.addChild(dim);
      const alpha = dress.dim;
      d.parts.push((v) => {
        const on = this.hudOn();
        dim.visible = on;
        const gr = dim.graphics;
        gr.clear();
        if (!on) return;
        gr.beginFill(0, alpha);
        gr.drawRect(v.x, v.y, v.width, v.height);
        gr.endFill();
      });
    }
    // Panels cut in three: the sides move out, the body column stretches between.
    for (const spec of dress.panels ?? []) {
      const c = this.findChild(root, spec.id);
      if (!c) continue;
      const b = boundsIn(c);
      const names = ['edge_l', 'fill_l', 'fill_r', 'edge_r'];
      const ps = names.map((n) => piece(c, `${n}_${spec.id}`));
      let at = root.getChildIndex(c) + 1;
      for (const p of ps) if (p) root.addChildAt(p.sprite, at++);
      const [edgeL, fillL, fillR, edgeR] = ps;
      const E = spec.edge;
      const F = spec.fill;
      d.parts.push((v, wide) => {
        const L = wide && v.x < b.x - 0.01;
        const R = wide && v.x + v.width > b.x + b.width + 0.01;
        const h = { y: b.y, height: b.height };
        // (the stretched body reaches a pixel and a half over or under each moved edge, whose last columns are the
        // body's colour: where two clipped pieces only met, the pixel they share let the screen under them show through
        // as a line)
        edgeL?.place({ x: b.x, width: E, ...h }, L ? { x: v.x, width: E, ...h } : null);
        fillL?.place({ x: b.x + F, width: 1, ...h }, L ? { x: v.x + E - 1.5, width: b.x + E - (v.x + E) + 2, ...h } : null);
        const r0 = b.x + b.width;
        const vr = v.x + v.width;
        fillR?.place({ x: r0 - F - 1, width: 1, ...h }, R ? { x: r0 - E - 0.5, width: vr - E + 1.5 - (r0 - E - 0.5), ...h } : null);
        edgeR?.place({ x: r0 - E, width: E, ...h }, R ? { x: vr - E, width: E, ...h } : null);
      });
      if (!spec.top && !spec.bottom) continue;
      // Above and below: rows of the panel over its whole width.
      const rows = ['fill_t', 'fill_b', 'edge_t', 'edge_b'].map((n) => piece(c, `${n}_${spec.id}`));
      for (const p of rows) if (p) root.addChildAt(p.sprite, at++);
      const [fillT, fillB, edgeT, edgeB] = rows;
      d.parts.push((v, wide) => {
        const w = { x: b.x, width: b.width };
        const b1 = b.y + b.height;
        const v1 = v.y + v.height;
        const T = wide && spec.top && v.y < b.y - 0.01 ? spec.top : null;
        const B = wide && spec.bottom && v1 > b1 + 0.01 ? spec.bottom : null;
        // (the stretched rows reach a pixel and a half under the moved edges, whose last rows are the stretched row's
        // colours, and over the panel's own edge, its anti-aliased last row included: no seam where one piece meets the
        // next, whatever the screen's scale and a lowered render resolution blur)
        edgeT?.place({ y: b.y, height: T ? T[0] : 1, ...w }, T ? { y: v.y, height: T[0], ...w } : null);
        fillT?.place({ y: b.y + (T ? T[1] : 0), height: 1, ...w }, T ? { y: v.y + T[0] - 1.5, height: b.y + T[0] + 1.5 - (v.y + T[0] - 1.5), ...w } : null);
        const from = B ? b1 - (B[2] ?? B[0] + 1.5) : 0;
        fillB?.place({ y: b.y + (B ? B[1] : 0), height: 1, ...w }, B ? { y: from, height: v1 - B[0] + 1.5 - from, ...w } : null);
        edgeB?.place({ y: b1 - (B ? B[0] : 1), height: B ? B[0] : 1, ...w }, B ? { y: v1 - B[0], height: B[0], ...w } : null);
      });
    }
    // Dims drawn as one over the whole shown area, the panels on them as extension art.
    for (const spec of dress.dims ?? []) {
      const c = this.findChild(root, spec.id);
      const o = c ? this.artObject(spec.name) : null;
      if (!c || !o) continue;
      const dim = new Sprite();
      dim.name = `${PART}dim_${spec.id}`;
      dim.mouseEnabled = false;
      let at = root.getChildIndex(c) + 1;
      root.addChildAt(dim, at++);
      root.addChildAt(o, at++);
      const b = boundsIn(o);
      const [left, right] = spec.edges ? [piece(o, `edge_l_${spec.id}`), piece(o, `edge_r_${spec.id}`)] : [null, null];
      for (const p of [left, right]) if (p) root.addChildAt(p.sprite, at++);
      d.parts.push((v, wide) => {
        c.visible = !wide;
        o.visible = wide;
        dim.visible = wide;
        dim.graphics.clear();
        if (wide) {
          dim.graphics.beginFill(spec.color, spec.alpha);
          dim.graphics.drawRect(v.x, v.y, v.width, v.height);
          dim.graphics.endFill();
        }
        const x1 = b.x + b.width;
        const h = { y: b.y, height: b.height };
        left?.place({ x: b.x + 2, width: 1, ...h }, wide && v.x < b.x ? { x: v.x, width: b.x + 2 - v.x, ...h } : null);
        right?.place({ x: x1 - 3, width: 1, ...h }, wide && v.x + v.width > x1 ? { x: x1 - 2, width: v.x + v.width - x1 + 2, ...h } : null);
      });
    }
    // Children drawn taller below the window.
    const sh = this.stage.stageHeight;
    for (const spec of dress.taller ?? []) {
      const c = this.findChild(root, spec.id);
      const o = c ? this.artObject(spec.name) : null;
      if (!c || !o) continue;
      root.addChildAt(o, root.getChildIndex(c) + 1);
      const b = boundsIn(o);
      d.parts.push((v, wide) => {
        const cut = v.y + v.height - spec.gap;
        const on = wide && cut > sh - spec.gap + 0.01;
        c.visible = !on;
        o.visible = on;
        o.scrollRect = on ? new Rectangle(b.x, b.y, b.width, Math.min(b.height, cut - b.y)) : null;
        o.x = on ? b.x : 0;
        o.y = on ? b.y : 0;
      });
    }
    // Extension art of the screen's bundle.
    for (const a of dress.art ?? []) {
      const o = this.artObject(a.name);
      if (!o) continue;
      const below = a.above !== undefined ? this.findChild(root, a.above) : null;
      const over = a.under !== undefined ? this.findChild(root, a.under) : null;
      if (below) root.addChildAt(o, root.getChildIndex(below) + 1);
      else if (over) root.addChildAt(o, root.getChildIndex(over));
      else root.addChild(o);
      d.parts.push((_v, wide) => {
        o.visible = wide;
      });
    }
    return d;
  }

  // ---------------------------------------------------------------- the HUD

  /** The HUD's layout is on: a wide host shows the screen (Stage.visibleRect set, a 4:3 tablet's too). */
  private hudOn(): boolean {
    return !!this.stage.$visibleArea;
  }

  /** The touch controls' clusters in stage coordinates, or null without touch controls (FWP.touch_zones). */
  private touchZones(): Area[] | null {
    const f = this.fwp && this.fwp.touch_zones;
    if (typeof f !== 'function') return null;
    try {
      const z = f();
      return Array.isArray(z) ? z : [];
    } catch {
      return [];
    }
  }

  private static matches(part: HudGroup['parts'][number], c: any): boolean {
    return typeof part === 'string' ? c.name === part : typeof part === 'number' ? c.$charId === part : false;
  }

  /** Gathers the screen's HUD children into their groups' sprites (once, at the first layout with a wide host). */
  private buildHud(d: Dressed): HudBuilt[] {
    const root = d.root;
    const unclip = new Set(d.dress.unclip ?? []);
    const clipped = d.dress.clip !== false;
    const out: HudBuilt[] = [];
    const hidden = new Set<any>();
    for (const spec of d.dress.hud ?? []) {
      const found: Array<{ at: number; obj: any; clip: boolean | null }> = [];
      for (let i = 0; i < root.numChildren; i++) {
        const c = root.getChildAt(i);
        if (typeof c.name === 'string' && c.name.startsWith(PART)) continue;
        for (const part of spec.parts) {
          if (typeof part === 'object' && 'art' in part) {
            if (c.$charId !== part.above) continue;
            const bundle = this.art().find((b) => b.has(part.art));
            const o = bundle ? bundle.get_object(part.art) : null;
            if (!o) continue;
            o.name = PART + part.art;
            if ('mouseEnabled' in o) o.mouseEnabled = false;
            found.push({ at: i + 0.5, obj: o, clip: null });
          } else if (typeof part === 'object') {
            if (c.$charId !== part.id) continue;
            const p = piece(c, `hud_${spec.name}_${part.id}`);
            if (!p) continue;
            p.place(part.src, part.src);
            hidden.add(c);
            found.push({ at: i, obj: p.sprite, clip: null });
          } else if (WideScreen.matches(part, c)) {
            found.push({ at: i, obj: c, clip: unclip.has(c.name) ? null : clipped });
          }
        }
      }
      const sprite = new Sprite();
      sprite.name = PART + 'hud_' + spec.name;
      // Not a target of its own: a click on its art goes to the screen, as before.
      sprite.mouseEnabled = false;
      // Where its first child was (the log and the timer stay under the battle map's click area), above the extension
      // art (it covers the margin the groups move into).
      let at = found.length ? Math.min(...found.map((f) => f.at)) : root.numChildren;
      for (let i = 0; i < root.numChildren; i++) if (root.getChildAt(i).name?.startsWith?.(PART + 'wide_')) at = Math.max(at, i + 1);
      root.addChildAt(sprite, Math.min(at, root.numChildren));
      found.sort((a, b) => a.at - b.at);
      for (const f of found) sprite.addChild(f.obj);
      out.push({ spec, sprite, members: found.map((f) => ({ obj: f.obj, clip: f.clip })), hits: new Map(), known: new WeakSet(), mutes: new Map(), wrap: null, labels: null, aligns: null, lead: null, fits: null, fitLeft: Infinity, reach: null, covered: false, rect: null, solid: null, fold: null });
    }
    for (const c of hidden) c.visible = false;
    return out;
  }

  /** A child the game added to the screen after its HUD was gathered: into its group. True when it was one. */
  private gatherLate(d: Dressed, c: any): boolean {
    const g = d.hud!.find((h) => h.spec.parts.some((p) => WideScreen.matches(p, c)));
    if (!g) return false;
    g.sprite.addChild(c);
    const unclip = new Set(d.dress.unclip ?? []);
    const m = { obj: c, clip: unclip.has(c.name) ? null : d.dress.clip !== false };
    g.members.push(m);
    if (this.hudOn()) c.$designClip = false;
    else if (m.clip !== null) c.$designClip = m.clip;
    return true;
  }

  /** Whether a group shows anything (a group of hidden children takes no room). */
  private static shown(g: HudBuilt): boolean {
    return g.members.some((m) => m.obj.visible);
  }

  private static shownKey(d: Dressed): string {
    return d.hud ? d.hud.map((g) => (WideScreen.shown(g) ? '1' : '0')).join('') : '';
  }

  /**
   * After each frame's scripts: a HUD whose groups came or went (the game shows the replay button or the achievements'
   * announcement when it renders the screen) is laid out again; a narrowed log shows its newest lines; a text the game
   * wrote into a `fit` field is cut to it; the `fade` groups over the battle's current worm fade.
   */
  private readonly onFrame = (): void => {
    if (!this.hudOn()) return;
    let focus: Area | null | undefined;
    for (const d of this.dressed) {
      if (d.dress.custom?.frame && d.root.stage) d.dress.custom.frame(d.root, { stage: this.stage, fwp: this.fwp });
      if (!d.hud || !d.root.stage) continue;
      if (WideScreen.shownKey(d) !== d.shown) this.layoutHud(d);
      // A toggle placed new music or sound buttons: their hit areas.
      if (d.hud.some((g) => g.rect && g.spec.hit && WideScreen.buttons(g).some((b) => b.hit && !g.known.has(b.btn)))) this.layoutHud(d);
      for (const g of d.hud) {
        if (g.wrap && g.wrap.narrow) WideScreen.showNewest(g.wrap);
        if (g.fold && g.rect) g.fold.frame();
        if (g.lead && g.rect) WideScreen.placeLead(g, true);
        if (g.spec.under && g.rect) {
          const covered = this.screenOpen(g.spec.under);
          if (covered !== g.covered) {
            g.covered = covered;
            g.sprite.visible = !covered;
          }
        }
        if (g.spec.fit) {
          this.setFits(g, true);
          // New team names (an online opponent's): the log beside them narrows or widens to their text.
          const ink = g.rect ? WideScreen.ink(g) : null;
          const now = ink && g.reach ? intersect(ink, g.reach) ?? g.rect : null;
          const was = g.solid;
          if (now && (!was || Math.abs(now.x - was.x) > 0.5 || Math.abs(now.y - was.y) > 0.5 || Math.abs(now.width - was.width) > 0.5 || Math.abs(now.height - was.height) > 0.5)) this.layoutHud(d);
        }
        if (!g.spec.fade) continue;
        if (focus === undefined) focus = this.focus();
        const shown = g.rect ? WideScreen.shownArea(d, g) : null;
        const alpha = focus && shown && overlapArea(shown, focus) > 0 ? HUD_FADE : 1;
        // (alpha is kept in 1/256 steps)
        if (Math.abs(g.sprite.alpha - alpha) > 0.01) g.sprite.alpha = alpha;
      }
    }
  };

  /**
   * What a placed group shows (the screen's coordinates): the `fold` log's lines shown (null: none), the team bars down
   * to the last team's row (their box keeps four), else its rectangle. What the battle's groups fade over the current
   * worm for, and what the camera keeps it clear of (cameraKeep).
   */
  private static shownArea(d: Dressed, g: HudBuilt): Area | null {
    if (!g.rect) return null;
    if (g.fold) {
      const own = g.fold.shownRect();
      if (!own) return null;
      const p = d.root.globalToLocal(new Point(own.x, own.y));
      return { x: p.x, y: p.y, width: own.width, height: own.height };
    }
    let r = g.solid ?? g.rect;
    const rows = g.spec.fit ? g.members.map((m) => m.obj).filter((o) => g.spec.fit!.includes(o.name) && o.visible && String(o.text) !== '') : [];
    if (rows.length) {
      const bottom = Math.max(...rows.map((o) => boundsIn(o, d.root)).map((b) => b.y + b.height));
      if (bottom > r.y && bottom < r.y + r.height) r = { x: r.x, y: r.y, width: r.width, height: bottom - r.y };
    }
    return r;
  }

  /** The battle's current worm on the stage (FWP.hud_focus), or null. */
  private focus(): Area | null {
    const f = this.fwp && this.fwp.hud_focus;
    if (typeof f !== 'function') return null;
    try {
      return f.call(this.fwp) ?? null;
    } catch {
      return null;
    }
  }

  /** Whether one of these screens of the original is open (the game's screen stack: FWP._interf.cur_screen). */
  private screenOpen(names: string[]): boolean {
    const c = this.fwp && this.fwp._interf && this.fwp._interf.cur_screen;
    const n = c ? Number(c[0]) || 0 : 0;
    for (let i = 1; i <= n; i++) if (c[i] && names.includes(c[i].name)) return true;
    return false;
  }

  private static showNewest(w: NonNullable<HudBuilt['wrap']>): void {
    const t = String(w.field.text);
    if (t === w.text) return;
    w.src = t;
    if (w.rule) {
      const fitted = WideScreen.fitRules(w.field, t, w.rule);
      if (fitted !== t) w.field.text = fitted;
    }
    w.text = String(w.field.text);
    w.field.scrollV = w.field.maxScrollV;
  }

  /**
   * `text` (the field's text, laid out now) with each line made only of the character `ch` (two or more) no longer than
   * one line of the field holds: as many of it as fit between the field's 2 px gutters (its advance measured on such a
   * line as the field lays it out).
   */
  private static fitRules(field: any, text: string, ch: string): string {
    const esc = ch.replace(/[.*+?^${}()|[\]\\-]/g, '\\$&');
    const rule = new RegExp(`^(?:${esc}){2,}$`, 'gm');
    if (!rule.test(text)) return text;
    let advance = 0;
    const n = Number(field.numLines) || 0;
    for (let i = 0; i < n && !(advance > 0); i++) {
      const line = String(field.getLineText(i)).replace(/[\r\n]+$/, '');
      if (line.length < 2 || line !== ch.repeat(line.length)) continue;
      advance = field.getLineMetrics(i).width / line.length;
    }
    if (!(advance > 0)) return text;
    const fit = Math.max(1, Math.floor((field.width / (field.scaleX || 1) - 4.5) / advance));
    rule.lastIndex = 0;
    return text.replace(rule, (line) => (line.length > fit ? ch.repeat(fit) : line));
  }

  /**
   * A `fit` group's art (the screen's coordinates): its shown children's bounds, of a `fit` field only its text's
   * (the lines' extent across, the field's height), or null: nothing shown.
   */
  private static ink(g: HudBuilt): Area | null {
    const root = g.sprite.parent;
    if (!root) return null;
    const fit = new Set(g.spec.fit ?? []);
    let out: Area | null = null;
    for (const m of g.members) {
      const o = m.obj;
      if (!o.visible) continue;
      const b = boundsIn(o, root);
      if (!(b.width > 0 && b.height > 0)) continue;
      let r: Area | null = b;
      if (fit.has(o.name) && typeof o.getLineMetrics === 'function') {
        r = null;
        const lb = o.getBounds(o);
        const n = String(o.text) ? o.numLines : 0;
        let x0 = Infinity;
        let x1 = -Infinity;
        for (let i = 0; i < n; i++) {
          const lm = o.getLineMetrics(i);
          if (!(lm.width > 0)) continue;
          x0 = Math.min(x0, lb.x + lm.x);
          x1 = Math.max(x1, lb.x + lm.x + lm.width);
        }
        if (x1 > x0 && lb.width > 0) {
          const f = b.width / lb.width;
          r = { x: b.x + (x0 - lb.x) * f, y: b.y, width: (x1 - x0) * f, height: b.height };
        }
      }
      if (r) out = out ? union(out, r) : r;
    }
    return out;
  }

  /**
   * The group's buttons shown now (nested in its children), whether each is one of its `hit` buttons (named so, or in
   * a child named so), and the place of the child holding it among the group's (which lies over which).
   */
  private static buttons(g: HudBuilt): Array<{ btn: any; hit: boolean; z: number }> {
    const names = new Set(g.spec.hit ?? []);
    const out: Array<{ btn: any; hit: boolean; z: number }> = [];
    const walk = (o: any, hit: boolean, z: number): void => {
      if (!o || !o.visible) return;
      const h = hit || names.has(o.name);
      if (o.$isButton) {
        out.push({ btn: o, hit: h, z });
        return;
      }
      const n = typeof o.numChildren === 'number' ? o.numChildren : 0;
      for (let i = 0; i < n; i++) walk(o.getChildAt(i), h, z);
    };
    g.members.forEach((m) => walk(m.obj, false, m.obj.parent === g.sprite ? g.sprite.getChildIndex(m.obj) : 0));
    return out;
  }

  /** The part of the shown safe area the HUD may use: what the screen paints of it (`paint`), else all of it. */
  private hudArea(d: Dressed): Area {
    const s = this.stage.safeRect as Rectangle;
    const safe = { x: s.x, y: s.y, width: s.width, height: s.height };
    let painted: Area | null = null;
    for (const key of d.dress.paint ?? []) {
      const c = (typeof key === 'string' && this.findChild(d.root, PART + key)) || this.findChild(d.root, key);
      if (!c || !c.visible) continue;
      // (Not a sliver: the main map's side art reaches 3 px into the window, all of it in a portrait browser.)
      const b = intersect(boundsIn(c), safe);
      if (b && b.width > 4 && b.height > 4) painted = painted ? union(painted, b) : b;
    }
    return painted ?? safe;
  }

  /**
   * Places the HUD's groups for the shown area (or back where the original has them without a wide host: only the
   * backpack is hidden while the touch controls are on, a phone at ?wide=0 included).
   */
  private layoutHud(d: Dressed): void {
    if (!d.dress.hud?.length) return;
    const on = this.hudOn();
    const zones = this.touchZones();
    if (!d.hud) {
      if (!on && !(zones !== null && d.dress.hud.some((g) => g.noTouch))) return;
      d.hud = this.buildHud(d);
    }
    if (!on) {
      for (const g of d.hud) {
        g.sprite.visible = !(g.spec.noTouch && zones !== null);
        g.sprite.alpha = 1;
        g.sprite.scaleX = g.sprite.scaleY = 1;
        g.sprite.x = g.sprite.y = 0;
        g.rect = null;
        g.reach = null;
        g.covered = false;
        g.fitLeft = Infinity;
        for (const m of g.members) if (m.clip !== null) m.obj.$designClip = m.clip;
        this.setWrap(g, null, null);
        g.fold?.layout(false);
        this.setHits(g, null);
        this.setLabels(g, false);
        this.setAligns(g, false);
        this.setFits(g, false);
        if (g.spec.outline !== undefined) g.sprite.filters = [];
      }
      d.shown = '';
      return;
    }
    d.shown = WideScreen.shownKey(d);
    const st = this.stage;
    const css = st.cssScale > 0 ? st.cssScale : 1;
    const A = this.hudArea(d);
    // The room from each side of the safe area (less where the safe area lies in from the shown area).
    const s0 = st.safeRect as Rectangle;
    const v0 = st.visibleRect as Rectangle;
    const side = (inset: boolean) => (inset ? HUD_INSET_MARGIN_CSS : HUD_MARGIN_CSS) / css;
    const mL = side(s0.x > v0.x + 0.5);
    const mR = side(s0.x + s0.width < v0.x + v0.width - 0.5);
    const mT = side(s0.y > v0.y + 0.5);
    const mB = side(s0.y + s0.height < v0.y + v0.height - 0.5);
    const gap = HUD_GAP_CSS / css;
    const clear = HUD_CLEAR_CSS / css;
    // A group's own box keeps its room from the groups placed before it; those after it keep clear of where it is
    // (its art: the log beside the team names' text).
    const keepOf = (spec: HudGroup) => d.keep.filter((k) => k.own !== spec.name && !(k.own && placed.has(k.own))).map((k) => inflate(k.r, clear));
    /** The touch controls' clusters with the room a group keeps from them. */
    const controlsOf = (spec?: HudGroup) => (zones ?? []).map((r) => inflate(r, (spec?.clear ?? HUD_CLEAR_CSS) / css));
    // The battle's groups in line with the controls' clusters' outer edges where those are a little further in than
    // the margin (the clusters centred in a phone's margin, or 12 CSS px in).
    let marginL = mL;
    let marginR = mR;
    if (zones && zones.length) {
      const l = Math.min(...zones.map((z) => z.x)) - A.x;
      const r = A.x + A.width - Math.max(...zones.map((z) => z.x + z.width));
      if (l > mL && l <= HUD_ALIGN_CSS / css) marginL = l;
      if (r > mR && r <= HUD_ALIGN_CSS / css) marginR = r;
    }
    const placed = new Map<string, HudPlaced>();
    /** The scale a group aims at (its smallest control or text at `css` CSS px, at least its `min`, within 1..max). */
    const aim = (spec: HudGroup) => Math.max(1, Math.min(spec.max, Math.max(spec.min ?? 1, spec.css / (spec.size * css))));
    /**
     * The room of a `tc` group along the top (stage px): between the groups placed beside it, and leaving the groups
     * placed after it at the top corners their narrowest box at the scale they aim at (the log's lines narrow, the
     * team bars cannot).
     */
    const spanOf = (spec: HudGroup, h: number): [number, number] => {
      const sep = Math.max(gap / 2, (spec.room ?? 0) / css);
      let lo = A.x + (spec.clearControls ? marginL : mL);
      let hi = A.x + A.width - (spec.clearControls ? marginR : mR);
      const mid = spec.box.x + spec.box.width / 2;
      for (const p of placed.values()) {
        const r = p.rect;
        if (r.y >= A.y + mT + h || r.y + r.height <= A.y + mT) continue;
        if (r.x + r.width / 2 < mid) lo = Math.max(lo, r.x + r.width + sep);
        else hi = Math.min(hi, r.x - sep);
      }
      for (const g of d.hud!.slice(d.hud!.findIndex((x) => x.spec === spec) + 1)) {
        const o = g.spec;
        if (o.next || (o.at !== 'tl' && o.at !== 'tr') || !WideScreen.shown(g) || (!!o.noTouch && zones !== null)) continue;
        const need = Math.min(o.wrap?.min ?? o.box.width, o.box.width) * aim(o) + Math.max(gap / 2, (o.room ?? 0) / css);
        if (o.at === 'tl') lo = Math.max(lo, A.x + (o.clearControls ? marginL : mL) + need);
        else hi = Math.min(hi, A.x + A.width - (o.clearControls ? marginR : mR) - need);
      }
      return [lo, hi];
    };
    /**
     * The group's rectangle at scale k with its box `w` x `h` (stage px), at its corner (a bottom one above the touch
     * controls' cluster there; the top's middle, as near its own middle as its room allows) or next to another group;
     * null: no room (`strict`; else as near as the shown area allows).
     */
    const rectOf = (spec: HudGroup, k: number, w: number, h: number, at: HudSpot, strict = true): Area | null => {
      const left = spec.at === 'tl' || spec.at === 'bl';
      const top = spec.at !== 'bl' && spec.at !== 'br';
      const o = at ? placed.get(at.name) : undefined;
      const g = (spec.gap ?? HUD_GAP_CSS) / css;
      if (at && o) {
        // Lined up with its art, its gap from its taps (its buttons' hit areas beyond its art).
        const r = o.rect;
        const t = o.taps ?? r;
        if (at.side === 'below' || at.side === 'above') return { x: left ? r.x : r.x + r.width - w, y: at.side === 'below' ? t.y + t.height + g : t.y - g - h, width: w, height: h };
        return { x: at.side === 'left' ? t.x - g - w : t.x + t.width + g, y: top !== at.end ? r.y : r.y + r.height - h, width: w, height: h };
      }
      const l = spec.clearControls ? marginL : mL;
      const r = spec.clearControls ? marginR : mR;
      let x = left ? A.x + l : A.x + A.width - r - w;
      if (spec.at === 'tc') {
        const [lo, hi] = strict ? spanOf(spec, h) : [A.x + l, A.x + A.width - r];
        if (strict && w > hi - lo + 0.5) return null;
        x = Math.min(Math.max(spec.box.x + spec.box.width / 2 - w / 2, lo), hi - w);
      }
      let bottom = A.y + A.height - mB;
      const gz = Math.max(g, (spec.clear ?? 0) / css);
      if (!top && spec.clearControls) for (const z of zones ?? []) if (z.x < x + w && x < z.x + z.width) bottom = Math.min(bottom, z.y - gz);
      return { x, y: top ? Math.max(A.y, A.y + mT - (spec.lift ?? 0) * k) : bottom - h, width: w, height: h };
    };
    /** Its places: its corner (`corner`) and next to the groups of `next` placed; none: its corner. */
    const spots = (spec: HudGroup): HudSpot[] => {
      const s: HudSpot[] = [];
      for (const n of spec.next ?? []) {
        if (n === 'corner') s.push(null);
        else if (placed.has(n[0])) s.push({ name: n[0], side: n[1], end: n[2] === 'end' });
      }
      return s.length ? s : [null];
    };
    /**
     * Its box's sizes at scale k, the largest first: a `wrap` narrows it in steps of 2 stage px down to its `min`, then
     * shortens it (each height from the widest) in steps of 4 px of the original down to its `minH`.
     */
    const sizes = (spec: HudGroup, k: number): Array<[number, number]> => {
      const W = spec.box.width * k;
      const H = spec.box.height * k;
      if (!spec.wrap) return [[W, H]];
      const minW = Math.min(spec.wrap.min, spec.box.width) * k;
      const minH = Math.min(spec.wrap.minH ?? spec.box.height, spec.box.height) * k;
      const out: Array<[number, number]> = [];
      for (let h = H; ; h = Math.max(minH, h - 4 * k)) {
        for (let w = W; w > minW; w -= 2) out.push([w, h]);
        out.push([minW, h]);
        if (h <= minH) break;
      }
      return out;
    };
    const inside = (r: Area) => r.x >= A.x - 0.5 && r.y >= A.y - 0.5 && r.x + r.width <= A.x + A.width + 0.5 && r.y + r.height <= A.y + A.height + 0.5;
    /** Whether the group `spec` at `r` (scale k) fits among the groups placed so far (null: a place with no room). */
    const fitter = (spec: HudGroup): ((r: Area | null, k: number) => boolean) => {
      // What it may cover: the key objects as much as the original did; nothing of the groups before it, nor (a
      // battle) of the touch controls; nothing beyond the area it may use.
      const keep = keepOf(spec);
      // What the group at `r` (scale k) covers of `o`: its box's notch not counted.
      const n = spec.notch;
      const cover = (r: Area, k: number, o: Area) =>
        overlapArea(r, o) - (n ? overlapArea({ x: r.x + (n.x - spec.box.x) * k, y: r.y + (n.y - spec.box.y) * k, width: n.width * k, height: n.height * k }, o) : 0);
      const allowed = keep.reduce((sum, r) => sum + cover(spec.box, 1, r), 0) + 0.5;
      const others = [...placed.values()].map((p) => inflate(p.solid ?? p.taps ?? p.rect, Math.max(gap / 2, (spec.room ?? 0) / css)));
      const avoid = spec.clearControls ? [...others, ...controlsOf(spec)] : others;
      return (r, k) => !!r && inside(r) && keep.reduce((sum, o) => sum + cover(r, k, o), 0) <= allowed && avoid.every((o) => cover(r, k, o) <= 0.5);
    };
    /** Its largest scale (from k0 down, and widest box) at the place `at`, or null: none fits there. */
    const placeAt = (spec: HudGroup, k0: number, at: HudSpot, fits = fitter(spec)): { k: number; w: number; h: number } | null => {
      for (let k = k0; ; k = Math.max(1, k - 0.02)) {
        for (const [w, h] of sizes(spec, k)) if (fits(rectOf(spec, k, w, h, at), k)) return { k, w, h };
        if (k <= 1) return null;
      }
    };
    /**
     * Of its places, the one where it grows most, an earlier one while a later one grows it by less than HUD_SPOT_GAIN
     * more (the scanner stays beside surrender for a little more size elsewhere; the second of a pair, which must reach
     * the first's scale: the first of equals); null: none fits.
     */
    const bestPlace = (spec: HudGroup, k0: number): { k: number; w: number; h: number; at: HudSpot } | null => {
      const fits = fitter(spec);
      const gain = spec.pair ? 1e-9 : HUD_SPOT_GAIN;
      let best: { k: number; w: number; h: number; at: HudSpot } | null = null;
      for (const at of spots(spec)) {
        const found = placeAt(spec, k0, at, fits);
        if (found && (!best || found.k > best.k + gain)) best = { ...found, at };
      }
      return best;
    };
    // A group whose pair got a smaller scale is placed again with it (with what depends on it), one that gives room to
    // groups after it (`yieldTo`) a little smaller while they get less (the pairs anew then), as long as that helps
    // them: a yield after which none of them grew within a few steps (what holds them is something else: a key object
    // beside them, the screen's edge) is taken back, and that group gives no more.
    const caps = new Map<string, number>();
    const yields = new Map<string, number>();
    /** The yield on trial: the group, its yield before it (undefined: none), its groups' scales then, the steps taken. */
    let trial: { name: string; from: number | undefined; before: number[]; steps: number } | null = null;
    const done = new Set<string>();
    const scalesOf = (y: NonNullable<HudGroup['yieldTo']>) => y.names.map((n) => placed.get(n)?.k ?? 0);
    // The team names cut to their fields first (the field's own width: at any scale): their text's ink is measured.
    for (const g of d.hud) {
      if (!g.spec.fit) continue;
      g.fitLeft = Infinity;
      this.setFits(g, true);
    }
    for (let pass = 0; pass < 240; pass++) {
      placed.clear();
      let again = false;
      for (const g of d.hud) {
        const spec = g.spec;
        // Hidden in touch mode (the backpack), or nothing of it shown: no room taken (its sprite hidden, not left where
        // it was; it shows again at the layout its children's coming brings, before the frame is drawn).
        const hide = (!!spec.noTouch && zones !== null) || !WideScreen.shown(g);
        g.sprite.visible = !hide;
        for (const m of g.members) m.obj.$designClip = false;
        if (hide) continue;
        const pair = spec.pair ? placed.get(spec.pair) : undefined;
        const inStep = spec.step ? placed.get(spec.step.name) : undefined;
        const k0 = Math.max(1, Math.min(aim(spec), pair ? pair.k : Infinity, inStep ? inStep.k * spec.step!.ratio : Infinity, caps.get(spec.name) ?? Infinity, yields.get(spec.name) ?? Infinity));
        // The first of a pair: of its places, the one where both grow most (its partner placed right after it there;
        // an earlier one while a later one grows them by less than HUD_SPOT_GAIN more).
        const partner = d.hud.find((h) => h.spec.pair === spec.name && !(!!h.spec.noTouch && zones !== null) && WideScreen.shown(h));
        let best: { k: number; w: number; h: number; at: HudSpot } | null = null;
        if (partner) {
          // At each place the largest scale at which its partner fits too, at one of its own places.
          const q = partner.spec;
          const kq = Math.max(1, Math.min(aim(q), caps.get(q.name) ?? Infinity, yields.get(q.name) ?? Infinity));
          const fits = fitter(spec);
          let most = 0;
          for (const at of spots(spec)) {
            let both: { k: number; w: number; h: number } | null = null;
            for (let k = k0; !both; k = Math.max(1, k - 0.02)) {
              const own = sizes(spec, k).find(([w, h]) => fits(rectOf(spec, k, w, h, at), k));
              if (own) {
                placed.set(spec.name, { rect: rectOf(spec, k, own[0], own[1], at)!, k, w: own[0], h: own[1] });
                const kk = Math.min(k, kq);
                const fitsQ = fitter(q);
                if (spots(q).some((a) => sizes(q, kk).some(([w, h]) => fitsQ(rectOf(q, kk, w, h, a), kk)))) both = { k, w: own[0], h: own[1] };
                placed.delete(spec.name);
              }
              if (k <= 1) break;
            }
            if (both && (!best || both.k > most + HUD_SPOT_GAIN)) {
              best = { ...both, at };
              most = both.k;
            }
          }
          if (!best) best = bestPlace(spec, k0);
        } else best = bestPlace(spec, k0);
        if (!best) best = { k: 1, w: spec.box.width, h: spec.box.height, at: spots(spec)[0] };
        const rect = rectOf(spec, best.k, best.w, best.h, best.at) ?? rectOf(spec, best.k, best.w, best.h, best.at, false)!;
        g.sprite.scaleX = g.sprite.scaleY = best.k;
        g.sprite.x = rect.x - spec.box.x * best.k;
        g.sprite.y = rect.y - spec.box.y * best.k;
        // A `fit` group's fields may grow left into the room at the top's left (a `tc` group: up to the narrowest box
        // of the groups after it there), its art then reaching beyond its rect.
        let reach = rect;
        if (spec.fit) {
          const lo = spec.at === 'tc' ? Math.min(rect.x, spanOf(spec, best.h)[0]) : rect.x;
          reach = { x: lo, y: rect.y, width: rect.x + rect.width - lo, height: rect.height };
          g.fitLeft = (lo - g.sprite.x) / best.k;
          this.setFits(g, true);
        }
        g.reach = reach;
        const solid = spec.fit ? WideScreen.ink(g) : null;
        // Its buttons' hit areas in the room the groups placed before it leave (their taps: its gap from them).
        const g0 = (spec.gap ?? HUD_GAP_CSS) / css;
        const hits = spec.hit ? this.hitRects(d, g, [...[...placed.values()].map((p) => inflate(p.taps ?? p.rect, g0)), ...keepOf(spec), ...(spec.clearControls ? controlsOf() : [])], A, css) : null;
        const taps = hits ? [...hits.values()].reduce(union, rect) : null;
        placed.set(spec.name, { rect, k: best.k, w: best.w, h: best.h, ...(solid ? { solid: intersect(solid, reach) ?? rect } : {}), ...(hits && taps ? { hits, taps } : {}) });
        // A pair shares the smaller scale.
        if (pair && spec.pair && best.k < pair.k - 1e-9) {
          caps.set(spec.pair, best.k);
          again = true;
        }
      }
      // The pairs settle first.
      if (again) continue;
      if (trial) {
        const t = trial;
        const y = d.hud.find((h) => h.spec.name === t.name)!.spec.yieldTo!;
        const p = placed.get(t.name);
        if (scalesOf(y).some((k, i) => k > t.before[i] + 1e-6)) trial = null; // it helped: kept
        else if (!p || t.steps >= HUD_YIELD_TRIES || p.k <= (y.min ?? 1) + 1e-6) {
          if (t.from === undefined) yields.delete(t.name);
          else yields.set(t.name, t.from);
          done.add(t.name);
          trial = null;
          caps.clear();
          continue;
        }
      }
      for (const g of d.hud) {
        const y = g.spec.yieldTo;
        const p = placed.get(g.spec.name);
        if (!y || !p || done.has(g.spec.name) || (trial && trial.name !== g.spec.name) || p.k <= (y.min ?? 1) + 1e-6) continue;
        const short = y.names.some((n) => {
          const q = placed.get(n);
          const o = d.hud!.find((h) => h.spec.name === n)!.spec;
          return !!q && o.size * q.k * css < y.css - 1e-6 && q.k < aim(o) - 1e-6;
        });
        if (!short) continue;
        if (!trial) trial = { name: g.spec.name, from: yields.get(g.spec.name), before: scalesOf(y), steps: 0 };
        trial.steps++;
        yields.set(g.spec.name, Math.max(y.min ?? 1, p.k - HUD_YIELD_STEP));
        caps.clear();
        again = true;
        break;
      }
      if (!again) break;
    }
    // The narrowed log's lines; the buttons' hit areas.
    for (const g of d.hud) {
      const p = g.sprite.visible ? placed.get(g.spec.name) : undefined;
      g.rect = p ? p.rect : null;
      g.solid = p?.solid ?? null;
      if (!p) g.sprite.alpha = 1;
      // Its place kept under a screen over it (nothing else moves when the weapon sheet opens).
      g.covered = !!p && !!g.spec.under && this.screenOpen(g.spec.under);
      if (g.covered) g.sprite.visible = false;
      this.setWrap(g, p ? p.w / p.k : null, p ? p.h / p.k : null);
      if (g.spec.fold && g.wrap) {
        g.fold ??= new LogFold(g.wrap.field, g.sprite, g.spec.fold, g.spec.wrap?.rule ?? '-');
        g.fold.layout(!!p);
      }
      this.setHits(g, p?.hits?.size ? p.hits : null);
      g.known = new WeakSet(p && g.spec.hit ? WideScreen.buttons(g).map((b) => b.btn) : []);
      this.setLabels(g, true);
      this.setAligns(g, true);
      this.setFits(g, true);
      if (g.spec.outline !== undefined && !(g.sprite.filters?.length > 0)) g.sprite.filters = [new GlowFilter(g.spec.outline, 0.85, 3, 3, 4, 2)];
    }
  }

  /**
   * The `fit` fields' text (`on`): where it is wider than the field, the field grows left (its right edge kept: a
   * right-aligned name) as far as the group's `fitLeft`, then the text is cut and ends with an ellipsis (a text the
   * game writes later is fitted at the next frame); or the fields as the original has them, with the game's text.
   */
  private setFits(g: HudBuilt, on: boolean): void {
    const names = g.spec.fit;
    if (!names || (!on && !g.fits)) return;
    if (!g.fits) {
      const fields = g.members.map((m) => m.obj).filter((o) => names.includes(o.name) && typeof o.textWidth === 'number');
      g.fits = fields.map((field) => ({ field, full: String(field.text), shown: null, x: field.x, width: field.width, grow: 0 }));
    }
    for (const f of g.fits) {
      const t = String(f.field.text);
      // How far left of the field its text may reach (less than 0: from inside it).
      const reach = on && Number.isFinite(g.fitLeft) ? Math.max(f.x - g.fitLeft, -f.width) : 0;
      if (on && t === f.shown && Math.abs(reach - f.grow) < 0.01) continue;
      if (t !== f.shown) f.full = t; // written by the game (or not cut yet)
      if (f.field.x !== f.x || f.field.width !== f.width) {
        f.field.x = f.x;
        f.field.width = f.width;
      }
      if (t !== f.full) setRawText(f.field, f.full);
      f.grow = reach;
      if (!on) {
        f.shown = null;
        continue;
      }
      // The field's own width within its 2 px gutters (the group scales it; less where the room its group leaves ends
      // inside it: a group whose box keeps less than the field), and the room to its left.
      const sx = Math.abs(f.field.scaleX) || 1;
      const room = (f.width + Math.min(0, reach)) / sx - 4;
      const over = f.field.textWidth - room;
      if (over > 0) {
        // (1 px more than the text needs: its last glyph's edge)
        const add = Math.min(over + 1, Math.max(0, reach) / sx);
        const wider = add > 0.5 ? add : 0;
        if (wider) {
          f.field.x = f.x - wider * sx;
          f.field.width = f.width + wider * sx;
        }
        if (over > wider) setRawTextFit(f.field, f.full, f.full, room + wider);
      }
      f.shown = String(f.field.text);
    }
  }


  /**
   * Cuts the `wrap` field to its group's box `boxW` x `boxH` px (null or the full size: as the original has it):
   * narrower lines, fewer of them; the newest shown.
   */
  private setWrap(g: HudBuilt, boxW: number | null, boxH: number | null): void {
    const spec = g.spec.wrap;
    if (!spec) return;
    if (!g.wrap) {
      const field = g.members.map((m) => m.obj).find((o) => o.name === spec.name);
      if (!field) return;
      g.wrap = { field, width: field.width, height: field.height, wordWrap: !!field.wordWrap, narrow: false, text: '', src: null, rule: spec.rule ?? null };
    }
    const w = g.wrap;
    const cutW = boxW === null ? 0 : g.spec.box.width - boxW;
    const cutH = boxH === null ? 0 : g.spec.box.height - boxH;
    // The text the game wrote, where its rules were shortened for the width it had (fitted again to the new one).
    const own = () => {
      if (w.src !== null && String(w.field.text) === w.text) w.field.text = w.src;
      w.src = null;
    };
    if (cutW > 0.5 || cutH > 0.5) {
      w.field.wordWrap = cutW > 0.5 || w.wordWrap;
      w.field.width = w.width - Math.max(0, cutW);
      w.field.height = w.height - Math.max(0, cutH);
      own();
      w.narrow = true;
      w.text = '';
      WideScreen.showNewest(w);
    } else if (w.narrow) {
      w.field.width = w.width;
      w.field.height = w.height;
      w.field.wordWrap = w.wordWrap;
      own();
      w.field.scrollV = 1;
      w.narrow = false;
    }
  }

  /** The `alignRight` fields right-aligned (`on`; the text the game writes later too), or as the original has them. */
  private setAligns(g: HudBuilt, on: boolean): void {
    const names = g.spec.alignRight;
    if (!names || (!on && !g.aligns)) return;
    if (!g.aligns) {
      const fields = g.members.map((m) => m.obj).filter((o) => names.includes(o.name) && typeof o.setTextFormat === 'function');
      g.aligns = fields.map((field) => ({ field, align: String(field.defaultTextFormat.align), x: field.x, width: field.width }));
    }
    for (const a of g.aligns) {
      const x = on ? Math.min(a.x, g.spec.box.x) : a.x;
      if (a.field.x !== x) {
        a.field.x = x;
        a.field.width = a.width + a.x - x;
      }
      const align = on ? 'right' : a.align;
      if (String(a.field.defaultTextFormat.align) === align && String(a.field.getTextFormat().align) === align) continue;
      const f = new TextFormat();
      f.align = align;
      a.field.defaultTextFormat = f;
      a.field.setTextFormat(f);
    }
    WideScreen.placeLead(g, on, true);
  }

  /**
   * The `lead` child beside its field's text (`on`; again when the text changed or `force`), or back at its own place.
   */
  private static placeLead(g: HudBuilt, on: boolean, force = false): void {
    const spec = g.spec.lead;
    if (!spec || (!on && !g.lead)) return;
    if (!g.lead) {
      const objs = g.members.map((m) => m.obj);
      const obj = objs.find((o) => o.$charId === spec.part);
      const field = objs.find((o) => o.name === spec.field);
      if (!obj || !field) return;
      const b = boundsIn(obj);
      g.lead = { obj, field, x: obj.x, right: b.x + b.width, text: null };
    }
    const l = g.lead;
    if (!on) {
      l.obj.x = l.x;
      l.text = null;
      return;
    }
    const text = String(l.field.text);
    if (!force && text === l.text) return;
    l.text = text;
    let x0 = Infinity;
    const n = text ? Number(l.field.numLines) || 0 : 0;
    for (let i = 0; i < n; i++) {
      const m = l.field.getLineMetrics(i);
      if (m.width > 0) x0 = Math.min(x0, m.x);
    }
    if (!Number.isFinite(x0)) {
      l.obj.x = l.x;
      return;
    }
    // (the line metrics' x counts from the field's bounds' left edge)
    const b = l.field.getBounds(l.field.parent);
    const lb = l.field.getBounds(l.field);
    const left = b.x + x0 * (lb.width > 0 ? b.width / lb.width : 1);
    l.obj.x = l.x + Math.max(0, left - spec.gap - l.right);
  }

  /** The `labels` fields in the group's colour and size (`on`), or as the original has them. */
  private setLabels(g: HudBuilt, on: boolean): void {
    const spec = g.spec.labels;
    if (!spec || (!on && !g.labels)) return;
    if (!g.labels) {
      // In the group's objects and their buttons' states (each state may hold its own copy of the label).
      const found = new Set<any>();
      const walk = (o: any): void => {
        if (!o || found.has(o)) return;
        if (spec.chars.includes(o.$charId) && typeof o.setTextFormat === 'function') found.add(o);
        for (const st of [o.$upState, o.$overState, o.$downState]) walk(st);
        const n = typeof o.numChildren === 'number' ? o.numChildren : 0;
        for (let i = 0; i < n; i++) walk(o.getChildAt(i));
      };
      for (const m of g.members) walk(m.obj);
      g.labels = [...found].map((field) => {
        const f = field.getTextFormat();
        return { field, size: Number(f.size), color: Number(f.color) };
      });
    }
    for (const l of g.labels) l.field.setTextFormat(on ? new TextFormat(null, spec.size, spec.color) : new TextFormat(null, l.size, l.color));
  }

  /**
   * The `hit` buttons' hit areas (the screen's coordinates): each target's art (its whole rectangle; buttons whose art
   * mostly overlaps are one target) grown towards HUD_HIT_CSS a side, first across (up to the walls beside it: the
   * other groups' taps, the key objects; and its neighbours in its row), then up and down (the walls and the buttons
   * above and below its span), inside the area `A`. Between two neighbours the line lies where each gets what it needs
   * beyond the room on its other side (halfway where both have room, or neither); two whose art overlaps meet at the
   * upper one's art's edge. A side facing the area's edge within HUD_HIT_CSS (nothing between: the margin) grows first,
   * a side with no room gives its share to the other. The group's other buttons keep their art (they take part as
   * neighbours).
   */
  private hitRects(d: Dressed, g: HudBuilt, walls: Area[], A: Area, css: number): Map<any, Area> {
    const T = HUD_HIT_CSS / css;
    const targets: Array<{ art: Area; btns: any[]; hit: boolean; z: number }> = [];
    for (const { btn, hit, z } of WideScreen.buttons(g)) {
      const b = boundsIn(btn, d.root);
      if (!(b.width > 0 && b.height > 0)) continue;
      const t = targets.find((o) => overlapArea(o.art, b) > 0.5 * Math.min(o.art.width * o.art.height, b.width * b.height));
      if (!t) targets.push({ art: b, btns: [btn], hit, z });
      else {
        t.art = union(t.art, b);
        t.btns.push(btn);
        t.hit ||= hit;
        t.z = Math.max(t.z, z);
      }
    }
    const out = new Map<any, Area>();
    if (!targets.some((t) => t.hit)) return out;
    type Span = [number, number];
    const x = (r: Area): Span => [r.x, r.x + r.width];
    const y = (r: Area): Span => [r.y, r.y + r.height];
    const across = (a: Span, b: Span) => a[0] < b[1] && b[0] < a[1];
    /**
     * Each target's span along one axis (`along` of a rectangle), grown: `cross` its extent across (the walls and
     * neighbours across it count), `near` whether another target is its neighbour along this axis.
     */
    const pass = (along: (r: Area) => Span, lo0: number, hi0: number, cross: (i: number) => Span, crossOf: (r: Area) => Span, near: (i: number, j: number) => boolean): Span[] => {
      // The walls' limits on each side, and whether a side faces the area's edge (no wall between).
      const lim = targets.map((t, i) => {
        const [s, e] = along(t.art);
        let min = lo0;
        let max = hi0;
        for (const w of walls) {
          if (!across(cross(i), crossOf(w))) continue;
          const [ws, we] = along(w);
          if (we <= s) min = Math.max(min, we);
          else if (ws >= e) max = Math.min(max, ws);
        }
        return { min, max, edgeMin: min === lo0 && s - lo0 <= T, edgeMax: max === hi0 && hi0 - e <= T };
      });
      const need = (i: number) => (targets[i].hit ? Math.max(0, T - (along(targets[i].art)[1] - along(targets[i].art)[0])) : 0);
      /** The line between target i and target j after it. */
      const line = (i: number, j: number): number => {
        const [si, ei] = along(targets[i].art);
        const [sj, ej] = along(targets[j].art);
        if (ei > sj) return targets[i].z > targets[j].z ? ei : sj;
        const lo = ei + Math.max(0, need(i) - Math.max(0, si - lim[i].min));
        const hi = sj - Math.max(0, need(j) - Math.max(0, lim[j].max - ej));
        return lo <= hi ? Math.min(Math.max((ei + sj) / 2, lo), hi) : Math.min(Math.max((lo + hi) / 2, ei), sj);
      };
      return targets.map((t, i) => {
        let { min, max, edgeMin, edgeMax } = lim[i];
        const [s, e] = along(t.art);
        targets.forEach((o, j) => {
          if (j === i || !near(i, j)) return;
          const [os, oe] = along(o.art);
          if (os + oe < s + e) {
            const l = line(j, i);
            if (l > min) [min, edgeMin] = [l, false];
          } else {
            const l = line(i, j);
            if (l < max) [max, edgeMax] = [l, false];
          }
        });
        const a0 = Math.max(s, min);
        const b0 = Math.min(e, max);
        const want = t.hit ? Math.max(0, T - (b0 - a0)) : 0;
        const roomA = Math.max(0, a0 - min);
        const roomB = Math.max(0, max - b0);
        let a: number;
        let b: number;
        if (edgeMin && !edgeMax) {
          a = Math.min(want, roomA);
          b = Math.min(want - a, roomB);
        } else if (edgeMax && !edgeMin) {
          b = Math.min(want, roomB);
          a = Math.min(want - b, roomA);
        } else {
          a = Math.min(want / 2, roomA);
          b = Math.min(want - a, roomB);
          a = Math.min(want - b, roomA);
        }
        return [a0 - a, b0 + b];
      });
    };
    // Across first: its neighbours are the buttons of its row.
    const xs = pass(x, A.x, A.x + A.width, (i) => y(targets[i].art), y, (i, j) => across(y(targets[i].art), y(targets[j].art)));
    // Then up and down, its row's span as grown: the buttons above and below it.
    const ys = pass(y, A.y, A.y + A.height, (i) => xs[i], x, (i, j) => !across(y(targets[i].art), y(targets[j].art)) && across(xs[i], xs[j]));
    targets.forEach((t, i) => {
      if (!t.hit) return;
      const r = { x: xs[i][0], y: ys[i][0], width: xs[i][1] - xs[i][0], height: ys[i][1] - ys[i][0] };
      for (const b of t.btns) out.set(b, r);
    });
    return out;
  }

  /**
   * Gives the buttons of `rects` (SimpleButtons) a hit state of their own covering that rectangle (the screen's
   * coordinates; a transparent rectangle, never drawn), and keeps the group's other children there from taking the
   * taps; the others (all, with null) get the original's back.
   */
  private setHits(g: HudBuilt, rects: Map<any, Area> | null): void {
    for (const [btn, h] of g.hits) {
      if (rects && rects.has(btn)) continue;
      if (h.on) btn.hitTestState = h.orig;
      g.hits.delete(btn); // (a music button its clip no longer shows)
    }
    const root = g.sprite.parent;
    // What lies over a hit area without being a button of its own takes no taps there (the + buttons' hit areas reach
    // under the gold and diamonds amounts: a tap on the corner beside the + would go nowhere).
    const mute = new Set<any>();
    if (rects && root) {
      const areas = [...rects.values()];
      const mouse = ['click', 'mouseDown', 'mouseUp', 'mouseOver', 'mouseOut', 'rollOver', 'rollOut'];
      for (const m of g.members) {
        const o = m.obj;
        if (rects.has(o) || o.$isButton || o.buttonMode || !('mouseEnabled' in o)) continue;
        if (typeof o.contains === 'function' && [...rects.keys()].some((b) => o.contains(b))) continue; // (the music clip)
        if (typeof o.hasEventListener === 'function' && mouse.some((t) => o.hasEventListener(t))) continue;
        const b = boundsIn(o, root);
        if (areas.some((r) => overlapArea(r, b) > 0)) mute.add(o);
      }
      // Nor what the buttons' states hold (a button takes a tap on an interactive child of its state too: a label
      // reaching past its share of the room into the next button's).
      const walk = (o: any): void => {
        if (!o || mute.has(o)) return;
        if ('mouseEnabled' in o) mute.add(o);
        const n = typeof o.numChildren === 'number' ? o.numChildren : 0;
        for (let i = 0; i < n; i++) walk(o.getChildAt(i));
      };
      for (const btn of rects.keys()) for (const st of [btn.$upState, btn.$overState, btn.$downState]) walk(st);
    }
    for (const [o, was] of g.mutes) {
      if (mute.has(o)) continue;
      o.mouseEnabled = was.enabled;
      if (was.children !== undefined) o.mouseChildren = was.children;
      g.mutes.delete(o);
    }
    for (const o of mute) {
      if (g.mutes.has(o)) continue;
      g.mutes.set(o, { enabled: o.mouseEnabled, children: 'mouseChildren' in o ? o.mouseChildren : undefined });
      o.mouseEnabled = false;
      if ('mouseChildren' in o) o.mouseChildren = false;
    }
    if (!rects) return;
    for (const [btn, r] of rects) {
      let h = g.hits.get(btn);
      if (!h) {
        const own = new Sprite();
        own.name = PART + 'hit';
        h = { own, orig: btn.hitTestState, on: false };
        g.hits.set(btn, h);
      }
      const p0 = btn.globalToLocal(root.localToGlobal(new Point(r.x, r.y)));
      const p1 = btn.globalToLocal(root.localToGlobal(new Point(r.x + r.width, r.y + r.height)));
      const gr = h.own.graphics;
      gr.clear();
      gr.beginFill(0, 0);
      gr.drawRect(Math.min(p0.x, p1.x), Math.min(p0.y, p1.y), Math.abs(p1.x - p0.x), Math.abs(p1.y - p0.y));
      gr.endFill();
      if (!h.on) {
        btn.hitTestState = h.own;
        h.on = true;
      }
    }
  }

  /** A new instance of the extension art `name` (a linkage class of the art's bundles), named as a part, or null. */
  private artObject(name: string): any {
    const bundle = this.art().find((c) => c.has(name));
    const o = bundle ? bundle.get_object(name) : null;
    if (!o || typeof o.getBounds !== 'function') return null;
    o.name = PART + name;
    if ('mouseEnabled' in o) o.mouseEnabled = false;
    return o;
  }

  private layout(d: Dressed): void {
    const { area, wide } = this.view();
    for (const s of d.stretched) {
      const o = s.obj;
      if (!wide || !(s.b.width > 0 && s.b.height > 0)) {
        o.scaleX = s.sx;
        o.scaleY = s.sy;
        o.x = s.x;
        o.y = s.y;
        continue;
      }
      // The child's bounds as placed by the original (s.b) mapped onto the shown area, in the parent's coordinates.
      const kx = area.width / s.b.width;
      const ky = area.height / s.b.height;
      o.scaleX = s.sx * kx;
      o.scaleY = s.sy * ky;
      o.x = area.x + (s.x - s.b.x) * kx;
      o.y = area.y + (s.y - s.b.y) * ky;
    }
    for (const p of d.parts) p(area, wide);
    this.layoutHud(d);
    d.dress.custom?.layout(d.root, { stage: this.stage, fwp: this.fwp });
  }
}
